import { makeAutoObservable } from 'mobx';
import { Utils } from '../utils/Utils';
import { Closet } from '../core/Closet';
import { Attachment } from '../core/Attachment';
import { Obstacle } from '../core/Obstacle';
import { Unit } from '../core/Unit';
import { CanvasUtils } from '../utils/CanvasUtils';
import { ClosetUtils } from '../utils/ClosetUtils';
import { PdfUtils } from '../utils/PdfUtils';
import { ClosetMode, ObstacleType, AttachmentType } from '../config';
import { ButtonMode } from '../config';
import { message } from 'antd';
import { getIdToken } from '../utils/getIdToken';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { SERVER_BASE_URL } from '../__common/env';
import {
    DEFAULT_PAGE_SCALE,
    EXTRACT_TEXT_HEIGHT_PERCENTAGE,
    EXTRACT_TEXT_WIDTH_PERCENTAGE,
    ScaleSource,
    DEFAULT_SCALEFACTOR,
    SCALEFACTOR_FOR_CONVERSION,
} from '../constant';
import { DownloadUtils } from '../utils/DownloadUtils';
import { InternalWall } from '../core/InternalWall';

//TODO: Improve this
const plusIcon = new Image();
plusIcon.src = '/assets/icons/plus-icon.png';
let plusIconData;
plusIcon.onload = () => {
    plusIconData = plusIcon;
};

const minusIcon = new Image();
minusIcon.src = '/assets/icons/minus-icon.png';
let minusIconData;
minusIcon.onload = () => {
    minusIconData = minusIcon;
};

export class ClosetManager {
    _libState = null;
    _pdfFile = null;
    _pdfCanvasRef = null;
    _pdfDoc = null;
    _scaleOnPdf = 1;
    _selectedCloset = null;
    _closets = [];
    _attachments = [];
    _obstacles = [];
    _units = [];
    _annotationCanvas = null;
    _closetCanvas = null;
    _scaleForAnnotation = 1;
    _tolForDelete = 15;
    _closetMode = ClosetMode.EDIT;
    _attachmentEditMode = false;
    _scaleForZoom = 1;
    _enableZoom = true;
    _panMode = false;
    _selectedPageNo = null;
    _selectedPagesDetails = [];
    _pageScale = [];
    _coordinatesToSave = null;
    _showSnapPoints = false;
    _selectedEdgeEnds = [];
    _isSaving = false;
    _selectedAttachment = null;
    _translateX = 0;
    _translateY = 0;
    _start = { x: 0, y: 0 };
    _last = { x: 0, y: 0 };
    _divRef = null;
    _obstacleEditMode = false;
    _obstacleForm = false;
    _selectedClosetId = null;
    _obstacleCoords = null;
    _selectedObstacle = null;
    _selectedUnit = null;
    _enableEditObstacleForm = false;
    _obstaclePosData = {};
    _scaleForFit = 1;
    _presentUnits = [];
    _allUnitData = null;
    _isLoading = false;
    _canCalibrate = false;
    _calibrationData = [];
    _firstLoading = false;
    _lastEditedPointIndex = null;
    _initialSelectedCoords = null;
    _distanceLabel = true;
    _canObstacleMove = false;
    _canInternalWallMove = false;
    _internalWalls = [];
    _internalWallData = null;
    _internalWallForm = false;

    constructor(libState) {
        makeAutoObservable(this);
        this._libState = libState;
    }

    get pdfBuffer() {
        return this._pdfFile;
    }

    get obstaclePosData() {
        return this._obstaclePosData;
    }

    get enableEditObstacleForm() {
        return this._enableEditObstacleForm;
    }

    get pageScale() {
        return this._pageScale;
    }

    get obstacles() {
        return this._obstacles;
    }

    get selectedObstacle() {
        return this._selectedObstacle;
    }

    get attachmentEditMode() {
        return this._attachmentEditMode;
    }

    get selectedPageNo() {
        return this._selectedPageNo;
    }

    get obstacleCoords() {
        return this._obstacleCoords;
    }

    get pdfCanvasRef() {
        return this._pdfCanvasRef;
    }
    get closetCanvas() {
        return this._closetCanvas;
    }

    get annotationCanvas() {
        return this._annotationCanvas;
    }

    get pdfDoc() {
        return this._pdfDoc;
    }

    get obstacleForm() {
        return this._obstacleForm;
    }

    get panMode() {
        return this._panMode;
    }

    get scaleForZoom() {
        return this._scaleForZoom;
    }

    get showSnapPoints() {
        return this._showSnapPoints;
    }

    get selectedEdgeEnds() {
        return this._selectedEdgeEnds;
    }
    get canInternalWallMove() {
        return this._canInternalWallMove;
    }

    get isSaving() {
        return this._isSaving;
    }
    get divRef() {
        return this._divRef;
    }

    get selectedClosetId() {
        return this._selectedClosetId;
    }

    get obstacleEditMode() {
        return this._obstacleEditMode;
    }

    get units() {
        return this._units;
    }
    get internalWalls() {
        return this._internalWalls;
    }
    get internalWallData() {
        return this._internalWallData;
    }
    get scaleForFit() {
        return this._scaleForFit;
    }

    get allUnitData() {
        return this._allUnitData;
    }
    get presentUnits() {
        return this._presentUnits;
    }

    get isLoading() {
        return this._isLoading;
    }

    get canCalibrate() {
        return this._canCalibrate;
    }

    get calibrationData() {
        return this._calibrationData;
    }
    get internalWallForm() {
        return this._internalWallForm;
    }

    get lastEditedPointIndex() {
        return this._lastEditedPointIndex;
    }

    get initialSelectedCoords() {
        return this._initialSelectedCoords;
    }

    get distanceLabel() {
        return this._distanceLabel;
    }

    get canObstacleMove() {
        return this._canObstacleMove;
    }

    get firstLoading() {
        return this._firstLoading;
    }

    get start() {
        return this._start;
    }

    get last() {
        return this._last;
    }
    get selectedAttachment() {
        return this._selectedAttachment;
    }

    get scaleOnPdf() {
        return this._scaleOnPdf;
    }

    get pdfCanvasContext() {
        return this._pdfCanvasRef.getContext('2d');
    }

    get closets() {
        return this._closets;
    }

    get attachments() {
        return this._attachments;
    }

    get tolForDelete() {
        return this._tolForDelete;
    }

    get enableZoom() {
        return this._enableZoom;
    }

    get closetMode() {
        return this._closetMode;
    }

    get closetCanvasRect() {
        const closetCanvasRect = this.closetCanvas.getBoundingClientRect();
        return closetCanvasRect;
    }

    get selectedCloset() {
        return this._selectedCloset;
    }

    get translateX() {
        return this._translateX;
    }

    get translateY() {
        return this._translateY;
    }

    get selectedUnit() {
        return this._selectedUnit;
    }

    setUnitScale(id, scale) {
        const unit = this.units.find((u) => u._id === id);
        if (!unit) return;
        unit.setScaleOnPdf(scale);
    }
    getScaleForUnit(id) {
        const unit = this.units.find((u) => u.id === id);
        if (!unit) return;
        return unit.scaleOnPdf;
    }

    getScaleUnitByClosetId(closetId) {
        const closet = this.closets.find((u) => u.id === closetId);
        if (!closet) return;
        const unit = this.units.find((u) => u.id === closet.unitId);
        if (!unit) return;
        return unit.scaleOnPdf;
    }
    setPageScale(pageNo, pageScale, type) {
        if (this.pageScale.findIndex((p) => p.pageNo === pageNo) === -1) {
            const page = { pageNo, scale: pageScale, type };
            this.pageScale.push(page);
            return;
        }
        this.pageScale[
            this.pageScale.findIndex((p) => p.pageNo === pageNo)
        ].scale = pageScale;
        this.pageScale[
            this.pageScale.findIndex((p) => p.pageNo === pageNo)
        ].type = type;
    }

    setInternalWallData(internalWallData) {
        this._internalWallData = internalWallData;
    }
    setInternalForm(bool) {
        this._internalWallForm = bool;
    }
    setInternalWallMove(bool) {
        this._canInternalWallMove = bool;
    }
    getScaleForPage(pageNo) {
        if (this.pageScale.find((p) => p.pageNo === pageNo)) {
            return this.pageScale.find((p) => p.pageNo === pageNo).scale;
        }
        return null;
    }
    getScaleTypeForPage(pageNo) {
        if (this.pageScale.find((p) => p.pageNo === pageNo)) {
            return this.pageScale.find((p) => p.pageNo === pageNo).type;
        }
        return ScaleSource.DEFAULT;
    }

    setObstaclePosData(obstaclePosData) {
        this._obstaclePosData = obstaclePosData;
    }

    setScaleForZoom(scaleForZoom) {
        this._scaleForZoom = scaleForZoom;
    }

    setCanObstacleMove(canObstacleMove) {
        this._canObstacleMove = canObstacleMove;
    }

    setDistanceLabel(distanceLabel) {
        this._distanceLabel = distanceLabel;
    }

    setInitialSelectedCoords(initialSelectedCoords) {
        this._initialSelectedCoords = initialSelectedCoords;
    }
    setLastEditedPointIndex(lastEditedPointIndex) {
        this._lastEditedPointIndex = lastEditedPointIndex;
    }

    setCalibrationData(calibrationData) {
        this._calibrationData = calibrationData;
    }

    setCanCalibrate(canCalibrate) {
        this._canCalibrate = canCalibrate;
    }

    setIsLoading(isLoading) {
        this._isLoading = isLoading;
    }

    setPresentUnits(presentUnits) {
        this._presentUnits = presentUnits;
    }

    setAllUnitData(allUnitData) {
        this._allUnitData = allUnitData;
    }

    setScaleForFit(scaleForFit) {
        this._scaleForFit = scaleForFit;
    }

    setObstacleEditMode(obstacleEditMode) {
        this._obstacleEditMode = obstacleEditMode;
    }

    setSelectedClosetId(selectedClosetId) {
        this._selectedClosetId = selectedClosetId;
    }

    setDivRef(divRef) {
        this._divRef = divRef;
    }

    setEnableEditObstacleForm(enableEditObstacleForm) {
        this._enableEditObstacleForm = enableEditObstacleForm;
    }

    setObstacleCoords(obstacleCoords) {
        this._obstacleCoords = obstacleCoords;
    }

    setSelectedObstacle(selectedObstacle) {
        this._selectedObstacle = selectedObstacle;
    }

    setIsSaving(isSaving) {
        this._isSaving = isSaving;
    }
    setStart(x, y) {
        this._start.x = x;
        this._start.y = y;
    }
    setLast(x, y) {
        this._last.x = x;
        this._last.y = y;
    }

    setFirstLoading(firstLoading) {
        this._firstLoading = firstLoading;
    }

    setSelectedEdgeEnds(selectedEdgeEnds) {
        this._selectedEdgeEnds = selectedEdgeEnds;
    }

    setShowSnapPoints(showSnapPoints) {
        this._showSnapPoints = showSnapPoints;
    }

    setPanMode(inBool) {
        this._panMode = inBool;
    }

    setSelectedUnit(selectedUnit) {
        this._selectedUnit = selectedUnit;
    }

    setTranslateX(translateX) {
        this._translateX = translateX;
    }

    setTranslateY(translateY) {
        this._translateY = translateY;
    }

    setAttachmentEditMode(bool) {
        this._attachmentEditMode = bool;
    }
    setSelectedAttachment(selectedAttachment) {
        this._selectedAttachment = selectedAttachment;
    }

    setObstacleForm(bool) {
        this._obstacleForm = bool;
    }

    shouldDoorFormOpen = () => {
        if (
            !this._libState.drawingManager.isDoorModeOn &&
            this._libState.drawingManager.drawingForm
        ) {
            return true;
        } else if (
            this.closetMode === ClosetMode.EDIT &&
            this.attachmentEditMode
        ) {
            return true;
        } else {
            return false;
        }
    };

    shouldObstacleFormOpen = () => {
        if (this.closetMode === ClosetMode.OBSTACLE && this.obstacleForm) {
            return true;
        } else if (
            this.closetMode === ClosetMode.EDIT &&
            this.obstacleEditMode
        ) {
            return true;
        }
        return false;
    };

    shouldInternalWallFormOpen = () => {
        if (this.closetMode === ClosetMode.EDIT && this.internalWallForm) {
            return true;
        }
        return false;
    };
    async setSelectedPageNo(selectedPageNo) {
        this._selectedPageNo = Number(selectedPageNo);
        if (this._libState.viewManager.selectedMenu === 2) {
            this.setFirstLoading(true);
            this.clearClosetCanvas();
            await this.renderPage();
            await this.fitToScreen();
            await this.loadSnappingPoints();
            this.drawNewClosets();
            this.setFirstLoading(false);
            await this.fitToScreen();
        }
    }

    setScaleFactorByPageNo(pageNo, scaleFactor) {
        const selectedPageDetails = this._selectedPagesDetails.find(
            (page) => page.pageNo == pageNo,
        );
        if (selectedPageDetails) {
            selectedPageDetails.scaleFactor = scaleFactor;
            this._selectedPagesDetails = [...this._selectedPagesDetails];
        }
    }

    fetchTransformationScaleFactorByPageNo(pageNo) {
        const selectedPageDetails = this._selectedPagesDetails.find(
            (page) => page.pageNo == pageNo,
        );
        if (selectedPageDetails) {
            return selectedPageDetails.scaleFactor;
        } else {
            this._selectedPagesDetails.push({
                pageNo,
                scaleFactor: 1,
            });
            return 1;
        }
    }
    setDeleteMode() {
        this._closetMode =
            this._closetMode === ClosetMode.DELETE
                ? ClosetMode.EDIT
                : ClosetMode.DELETE;
        if (this.closetMode === ClosetMode.DELETE) {
            this.addMinusButton();
            this.addDeletePolygonButton();
        }
        if (this.closetMode === ClosetMode.EDIT) {
            this.drawNewClosets();
        }
    }
    setObstacleMode(bool) {
        if (bool) {
            this._closetMode = ClosetMode.OBSTACLE;
        } else {
            this._closetMode = ClosetMode.EDIT;
        }
    }
    setNoneMode() {
        this._closetMode =
            this._closetMode === ClosetMode.NONE
                ? ClosetMode.EDIT
                : ClosetMode.NONE;
    }

    setEditMode() {
        this._closetMode = ClosetMode.EDIT;
    }

    setEnableZoom(enableZoom) {
        this._enableZoom = enableZoom;
    }

    setAttachmentAttributes(attachmentType, type, openingType, swing) {
        const lastAttachment = this.attachments[this.attachments.length - 1];
        if (lastAttachment) {
            lastAttachment.setAttachmentType(attachmentType);
            lastAttachment.setType(type);
            lastAttachment.setOpening(openingType);
            lastAttachment.setSwing(swing);
        }
        this._attachments = [...this._attachments];
    }

    updateAttachmentAttributes(attachmentType, type, openingType, swing) {
        const attachment = this.attachments.find(
            (attachment) => attachment.id === this.selectedAttachment.id,
        );
        if (attachment) {
            attachment.setAttachmentType(attachmentType);
            attachment.setType(type);
            attachment.setOpening(openingType);
            attachment.setSwing(swing);
            this.setSelectedAttachment(null);
        }
        this._attachments = [...this._attachments];
    }
    updateSelectedObstacle(obstacleData) {
        if (!obstacleData || !this.selectedObstacle) {
            console.warn(
                'Invalid obstacleData or no selectedObstacle:',
                obstacleData,
            );
            return;
        }

        const obstacle = this.selectedObstacle;
        const closet = this.closets.find((c) => c.id === obstacle.closetId);
        if (!closet) return;

        /* ---------------------------- validation size vs closet ---------------------------- */
        const closetScale = this.getScaleFactorForAnnotationDisplayByClosetId(
            this.selectedPageNo,
            closet.id,
        );
        const minEdge = ClosetUtils.minEdgeLengthInches(
            closet.coordinates,
            closetScale,
        );

        const dimsInches = {
            width: Utils.parseInches(obstacleData.width || "0' 0"),
            height: Utils.parseInches(obstacleData.height || "0' 0"),
            radius: Utils.parseInches(obstacleData.radius || "0' 0"),
        };

        const { valid, msg } = ClosetUtils.validateObstacleSize(
            obstacleData.obstacleType,
            dimsInches,
            minEdge,
        );
        if (!valid) {
            message.error(msg);
            return false;
        }

        // dimension positivity checks
        if (
            obstacleData.obstacleType === 'RECTANGLE' &&
            (dimsInches.width <= 0 || dimsInches.height <= 0)
        )
            return;
        if (obstacleData.obstacleType === 'CIRCLE' && dimsInches.radius <= 0)
            return;

        /* ---------------------------- apply scaling ---------------------------- */
        const scaleFactor = this.getScaleFactorForAnnotationDisplayByClosetId(
            this.selectedPageNo,
            obstacle.closetId,
            true,
        );
        const scaledDims = ClosetUtils.scaledObstacleData(
            dimsInches,
            scaleFactor,
        );

        obstacle.setData({
            ...obstacle.data,
            ...scaledDims,
            rotation: obstacleData.rotation || 0,
        });
        obstacle.setObstacleType(obstacleData.obstacleType);

        this.setSelectedObstacle(null);
        this._obstacles = [...this.obstacles];
        this.drawNewClosets();
        return true;
    }

    setTolForDelete = (tolForDelete) => {
        this._tolForDelete = tolForDelete;
    };

    setScaleOnChange(scaleOnPdf) {
        this._selectedPagesDetails.find(
            (page) => page.pageNo === this.selectedPageNo,
        ).scaleFactor = scaleOnPdf;
    }

    setPdfBuffer = (pdfBuffer) => {
        this._pdfFile = pdfBuffer;
    };

    setPdfCanvasRef = (canvasRef) => {
        this._pdfCanvasRef = canvasRef;
    };

    setAnnotationCanvas = (annotationCanvas) => {
        this._annotationCanvas = annotationCanvas;
    };

    setClosetCanvas = (closetCanvas) => {
        this._closetCanvas = closetCanvas;
    };

    setPdfDoc = (pdfDoc) => {
        this._pdfDoc = pdfDoc;
    };

    setScaleOnPdf = (scaleOnPdf) => {
        this._scaleOnPdf = scaleOnPdf;
    };

    zoomIn(x, y) {
        const closetCanvasRect = this.closetCanvasRect;
        const mouseX = x - closetCanvasRect.left;
        const mouseY = y - closetCanvasRect.top;
        if (this.panMode) return;
        if (this._libState.drawingManager.isInternalWallModeOn) return;
        if (this.enableZoom) {
            let scaleFactor = this.fetchTransformationScaleFactorByPageNo(
                this.selectedPageNo,
            );
            const xTrans =
                this.translateX -
                (mouseX - this.translateX) *
                    ((scaleFactor + 0.1) / scaleFactor - 1);
            const yTrans =
                this.translateY -
                (mouseY - this.translateY) *
                    ((scaleFactor + 0.1) / scaleFactor - 1);
            this.setTranslateX(xTrans);
            this.setTranslateY(yTrans);
            this._scaleForZoom = scaleFactor / (scaleFactor + 0.1);
            scaleFactor += 0.1;

            this.setScaleFactorByPageNo(this.selectedPageNo, scaleFactor);
            if (
                this._libState.drawingManager.isDrawingModeOn ||
                this._libState.drawingManager.isDoorModeOn ||
                this._libState.drawingManager.isUnitModeOn ||
                this._libState.drawingManager.isCalibrationModeOn ||
                this._libState.drawingManager.isRectangleModeOn
            ) {
                if (this._libState.drawingManager.closetPolygon.length > 0) {
                    this._libState.drawingManager.scaleDrawnPolygon(
                        this._scaleForZoom,
                    );
                }
                if (this._libState.drawingManager.isRectangleModeOn) {
                    this._libState.drawingManager._rectangleStartPoint =
                        this._libState.drawingManager._closetPolygon[0];
                }
            }
            this.clearClosetCanvas();
            this.renderPage();
            this.drawAllClosets();
            this.drawSnapPoints();
            this.scaleInitialSelectedCoords();
            if (this.closetMode === ClosetMode.DELETE) {
                this.addMinusButton();
                this.addDeletePolygonButton();
            }
        }
    }

    scaleInitialSelectedCoords() {
        if (!this.initialSelectedCoords) return;
        const coords = this.initialSelectedCoords;
        for (let i = 0; i < coords.length; i++) {
            coords[i].x /= this.scaleForZoom;
            coords[i].y /= this.scaleForZoom;
        }
        this.setInitialSelectedCoords(coords);
    }

    async zoomForFit(scale) {
        const closetCanvasRect = this.closetCanvasRect;
        const mouseX = -closetCanvasRect.left;
        const mouseY = -closetCanvasRect.top;
        let scaleFactor = this.fetchTransformationScaleFactorByPageNo(
            this.selectedPageNo,
        );
        const xTrans =
            this.translateX -
            (mouseX - this.translateX) * (scale / scaleFactor - 1);
        const yTrans =
            this.translateY -
            (mouseY - this.translateY) * (scale / scaleFactor - 1);
        this.setTranslateX(xTrans);
        this.setTranslateY(yTrans);
        this._scaleForZoom = scaleFactor / scale;
        scaleFactor = scale;

        this.setScaleFactorByPageNo(this.selectedPageNo, scaleFactor);
        if (
            this._libState.drawingManager.isDrawingModeOn ||
            this._libState.drawingManager.isDoorModeOn ||
            this._libState.drawingManager.isUnitModeOn
        ) {
            if (this._libState.drawingManager.closetPolygon.length > 0) {
                this._libState.drawingManager.scaleDrawnPolygon(
                    this._scaleForZoom,
                );
            }
        }
        this.clearClosetCanvas();
        this.renderPage();
        this.drawAllClosets();
        if (this.closetMode === ClosetMode.DELETE) {
            this.addMinusButton();
            this.addDeletePolygonButton();
        }
    }

    calculatePanRendering(x, y) {
        const xTrans = this.last.x + (x - this.start.x);
        const yTrans = this.last.y + (y - this.start.y);
        this.setTranslateX(xTrans);
        this.setTranslateY(yTrans);

        this.clearClosetCanvas();
        this.renderPage();

        this._libState.drawingManager.transformAnnotationCanvas();
        this.drawNewClosets();
        this.drawSnapPoints();
        if (this.closetMode === ClosetMode.DELETE) {
            this.addMinusButton();
            this.addDeletePolygonButton();
        }
    }
    zoomOut(x, y) {
        const closetCanvasRect = this.closetCanvasRect;
        const mouseX = x - closetCanvasRect.left;
        const mouseY = y - closetCanvasRect.top;
        if (this.panMode) return;
        if (this._libState.drawingManager.isInternalWallModeOn) return;
        if (this.enableZoom) {
            let scaleFactor = this.fetchTransformationScaleFactorByPageNo(
                this.selectedPageNo,
            );
            if (scaleFactor - 0.1 <= this.scaleForFit - 0.1)
                return console.error('Zoom out limit reached');
            const xTrans =
                this.translateX -
                (mouseX - this.translateX) *
                    ((scaleFactor - 0.1) / scaleFactor - 1);
            const yTrans =
                this.translateY -
                (mouseY - this.translateY) *
                    ((scaleFactor - 0.1) / scaleFactor - 1);
            this.setTranslateX(xTrans);
            this.setTranslateY(yTrans);
            this._scaleForZoom = scaleFactor / (scaleFactor - 0.1);
            scaleFactor -= 0.1;

            this.setScaleFactorByPageNo(this.selectedPageNo, scaleFactor);
            if (
                this._libState.drawingManager.isDrawingModeOn ||
                this._libState.drawingManager.isDoorModeOn ||
                this._libState.drawingManager.isUnitModeOn ||
                this._libState.drawingManager.isCalibrationModeOn ||
                this._libState.drawingManager.isRectangleModeOn
            ) {
                if (this._libState.drawingManager.closetPolygon.length > 0) {
                    this._libState.drawingManager.scaleDrawnPolygon(
                        this._scaleForZoom,
                    );
                }
                if (this._libState.drawingManager.isRectangleModeOn) {
                    this._libState.drawingManager._rectangleStartPoint =
                        this._libState.drawingManager._closetPolygon[0];
                }
            }
            this.clearClosetCanvas();
            this.renderPage();
            this.drawAllClosets();
            this.drawSnapPoints();
            this.scaleInitialSelectedCoords();
            if (this.closetMode === ClosetMode.DELETE) {
                this.addMinusButton();
                this.addDeletePolygonButton();
            }
        }
    }

    updateClosetName(closetId, newName) {
        const closet = this._closets.find((closet) => closet.id === closetId);
        if (closet) {
            closet.setName(newName);
            this._closets = [...this._closets];
        }
    }

    async fitToScreen() {
        const closetCanvasRect = this.pdfCanvasRef.getBoundingClientRect();
        const containerWidth = closetCanvasRect.width;
        const containerHeight = closetCanvasRect.height;
        const pageNo = this.selectedPageNo;
        if (!this.pdfDoc) {
            const pdfDoc = await Utils.readPdf(this.pdfBuffer);
            this.setPdfDoc(pdfDoc);
        }
        const page = await this.pdfDoc.getPage(pageNo);
        const contentWidth = page._pageInfo.view[2];
        const contentHeight = page._pageInfo.view[3];
        const scaleX = containerWidth / contentWidth;
        const scaleY = containerHeight / contentHeight;
        const scaleFactor = Math.min(scaleX, scaleY);
        const contentCenterX = (contentWidth / 2) * scaleFactor;
        const contentCenterY = (contentHeight / 2) * scaleFactor;

        const viewportCenterX = containerWidth / 2;
        const viewportCenterY = containerHeight / 2;

        const transX = viewportCenterX - contentCenterX;
        const transY = viewportCenterY - contentCenterY;

        this.setTranslateX(transX);
        this.setTranslateY(transY);

        this.renderPage();

        await this._libState.drawingManager.transformAnnotationCanvas();
        this.setScaleForFit(scaleFactor);
        this.zoomForFit(scaleFactor);
        this.setScaleFactorByPageNo(this.selectedPageNo, scaleFactor);
        this.clearClosetCanvas();

        this.drawNewClosets();
    }

    addNewCloset(id) {
        const closet = new Closet(id, this.selectedPageNo, this.scaleOnPdf);
        this._closets.push(closet);
    }

    addNewAttachment(id) {
        const attachment = new Attachment(
            id,
            this.selectedPageNo,
            this.scaleOnPdf,
        );
        this._attachments.push(attachment);
    }

    addObstacleAtPoint(coordinate) {
        let selectedCloset = null;
        const context = this.closetCanvas.getContext('2d');
        const closetCanvasRect = this.closetCanvasRect;
        coordinate.x = coordinate.x - closetCanvasRect.left - this.translateX;
        coordinate.y = coordinate.y - closetCanvasRect.top - this.translateY;
        for (const closet of this._closets) {
            if (closet.pageNo !== this.selectedPageNo) continue;
            if (Utils.pointInPolygon(coordinate, closet.coordinates)) {
                selectedCloset = closet;
                this.setObstacleForm(true);
                break;
            }
        }

        if (!selectedCloset) return;
        this.setObstacleCoords(coordinate);
        this.setSelectedClosetId(selectedCloset.id);

        CanvasUtils.drawLines(
            context,
            selectedCloset.coordinates,
            'yellow',
            10,
        );
    }

    addExistingObstacle(givenData, pageNum, scale, closetId) {
        const obstacle = new Obstacle(
            givenData.id,
            closetId,
            pageNum,
            scale,
            givenData.data,
            givenData.type,
        );
        this._obstacles.push(obstacle);
    }

    addExistingInternalWall(givenData, pageNum, scale, closetId) {
        const internalWall = new InternalWall(
            givenData.id,
            closetId,
            pageNum,
            scale,
            givenData.length,
            givenData.depth,
            givenData.rotation,
            givenData.coordinates,
            givenData.normal,
        );
        this._internalWalls.push(internalWall);
    }
    addObstacle(data) {
        if (!data || !this.obstacleCoords || !this.selectedClosetId) {
            console.warn('Invalid data, obstacleCoords, or selectedClosetId:', {
                data,
                obstacleCoords: this.obstacleCoords,
                selectedClosetId: this.selectedClosetId,
            });
            return;
        }

        const coordinate = this.obstacleCoords;
        data['coordinates'] = coordinate;

        const widthInches = Utils.parseInches(data.width || "0' 0");
        const heightInches = Utils.parseInches(data.height || "0' 0");
        const radiusInches = Utils.parseInches(data.radius || "0' 0");
        const rotation = parseFloat(data.rotation) || 0;

        if (
            data.obstacleType === 'RECTANGLE' &&
            (widthInches <= 0 || heightInches <= 0)
        ) {
            console.warn('Invalid rectangle dimensions:', {
                widthInches,
                heightInches,
            });
            return;
        }
        if (data.obstacleType === 'CIRCLE' && radiusInches <= 0) {
            console.warn('Invalid circle radius:', radiusInches);
            return;
        }

        const scaleFactor = this.getScaleFactorForAnnotationDisplayByClosetId(
            this.selectedPageNo,
            this.selectedClosetId,
            true,
        );
        const closet = this.closets.find((c) => c.id === this.selectedClosetId);
        const closetPolygon = closet.coordinates;
        const scale = this.getScaleFactorForAnnotationDisplayByClosetId(
            this.selectedPageNo,
            closet.id,
        );
        let minLength = Infinity;
        for (let i = 0; i < closetPolygon.length; i++) {
            const point1 = closetPolygon[i];
            const point2 = closetPolygon[(i + 1) % closetPolygon.length];
            const length = CanvasUtils.measureDistance(point1, point2, scale);
            minLength = Math.min(minLength, length);
        }
        if (data.obstacleType === ObstacleType.RECTANGLE) {
            if (
                minLength < widthInches * 0.9 ||
                minLength < heightInches * 0.9
            ) {
                message.error('Obstacle size cannot greater than closet');
                return false;
            }
        }
        if (data.obstacleType === ObstacleType.CIRCLE) {
            if (minLength < radiusInches * 0.9) {
                message.error('Obstacle size maybe greater than closet');
                return false;
            }
        }
        const savedData = {
            coordinate: coordinate,
            width: widthInches * scaleFactor * SCALEFACTOR_FOR_CONVERSION,
            height: heightInches * scaleFactor * SCALEFACTOR_FOR_CONVERSION,
            radius: radiusInches * scaleFactor * SCALEFACTOR_FOR_CONVERSION,
            rotation: rotation,
        };

        const obstacle = new Obstacle(
            Utils.generateUuid(),
            this.selectedClosetId,
            this.selectedPageNo,
            this.scaleOnPdf,
            savedData,
            data.obstacleType,
        );

        this._obstacles.push(obstacle);
        if (!radiusInches) {
            CanvasUtils.drawRectangleForObstacles(
                this.closetCanvas.getContext('2d'),
                coordinate,
                heightInches,
                widthInches,
                rotation,
                'green',
                0.5,
            );
        } else {
            CanvasUtils.drawCircle(
                this.closetCanvas.getContext('2d'),
                coordinate,
                radiusInches,
                'red',
                0.5,
            );
        }
        if (!this.distanceLabel) {
            this.setDistanceLabel(true);
        }

        this.drawNewClosets();

        this.setObstacleMode(false);
    }

    drawPositionLines(id, isInternalWall = false) {
        const context = this.closetCanvas.getContext('2d');
        const item = isInternalWall
            ? this.internalWalls.find((wall) => wall.id === id)
            : this.obstacles.find((obstacle) => obstacle.id === id);

        if (!item) return;

        const closet = this.closets.find(
            (closet) => closet.id === item.closetId,
        );
        if (!closet) return;

        const { x: centerX, y: centerY } =
            item.coordinate || item.data.coordinate;
        const closetPolygon = closet.coordinates;

        const width = isInternalWall
            ? Math.abs(item.normal?.x || 0) > Math.abs(item.normal?.y || 0)
                ? item.length
                : item.depth
            : (item.data.width ?? 0);

        const height = isInternalWall
            ? Math.abs(item.normal?.x || 0) > Math.abs(item.normal?.y || 0)
                ? item.depth
                : item.length
            : (item.data.height ?? 0);

        const radius = isInternalWall ? 0 : (item.data.radius ?? 0);

        let horizontalLine = [];
        let verticalLine = [];

        if (!radius) {
            horizontalLine = [
                { x: centerX - width / 2, y: centerY },
                { x: centerX + width / 2, y: centerY },
            ];

            verticalLine = [
                { x: centerX, y: centerY - height / 2 },
                { x: centerX, y: centerY + height / 2 },
            ];
        } else {
            horizontalLine = [
                { x: centerX - radius, y: centerY },
                { x: centerX + radius, y: centerY },
            ];

            verticalLine = [
                { x: centerX, y: centerY - radius },
                { x: centerX, y: centerY + radius },
            ];
        }

        const scaleFactor = this.getScaleFactorForAnnotationDisplayByClosetId(
            this.selectedPageNo,
            closet.id,
        );

        let horizontalIntersection = [];
        let verticalIntersection = [];

        closetPolygon.forEach((coord, i) => {
            const nextCoord = closetPolygon[(i + 1) % closetPolygon.length];

            const hIntersection = CanvasUtils.findIntersection(horizontalLine, [
                coord,
                nextCoord,
            ]);
            if (hIntersection) horizontalIntersection.push(hIntersection);

            const vIntersection = CanvasUtils.findIntersection(verticalLine, [
                coord,
                nextCoord,
            ]);
            if (vIntersection) verticalIntersection.push(vIntersection);
        });

        const drawMeasurementLines = (line1, line2, bColor, tColor) => {
            CanvasUtils.drawLines(context, line1, 'grey');
            CanvasUtils.drawLines(context, line2, 'grey');
            if (this.distanceLabel) {
                CanvasUtils.drawDistanceLabel(
                    context,
                    line1[0],
                    line1[1],
                    scaleFactor,
                    bColor,
                    tColor,
                );
                CanvasUtils.drawDistanceLabel(
                    context,
                    line2[0],
                    line2[1],
                    scaleFactor,
                    bColor,
                    tColor,
                );
            }
        };

        const diff = radius || width / 2;

        const bColor = radius
            ? '#C5E1A5'
            : isInternalWall
              ? '#BBDEFB'
              : '#FFD580';
        const tColor = radius
            ? '#1B5E20'
            : isInternalWall
              ? '#0D47A1'
              : '#5A3E1B';

        if (horizontalIntersection.length === 2) {
            horizontalIntersection.sort((a, b) => a.x - b.x);

            const line1 = [
                horizontalIntersection[0],
                { x: centerX - diff, y: centerY },
            ];
            const line2 = [
                horizontalIntersection[1],
                { x: centerX + diff, y: centerY },
            ];

            drawMeasurementLines(line1, line2, bColor, tColor);
        }

        if (verticalIntersection.length === 2) {
            const diffV = radius || height / 2;
            verticalIntersection.sort((a, b) => a.y - b.y);

            const line1 = [
                { x: centerX, y: centerY - diffV },
                verticalIntersection[0],
            ];
            const line2 = [
                { x: centerX, y: centerY + diffV },
                verticalIntersection[1],
            ];

            drawMeasurementLines(line1, line2, bColor, tColor);
        }
    }

    fetchClosetIdByAttachmentCoordinates(coordinates, selectedPageNo) {
        const [p1] = coordinates;

        for (const closet of this._closets) {
            if (closet.pageNo !== selectedPageNo) continue;
            for (let i = 0; i < closet.coordinates.length; i++) {
                const a = closet.coordinates[i % closet.coordinates.length];
                const b =
                    closet.coordinates[(i + 1) % closet.coordinates.length];
                const dist1 = Math.sqrt((a.x - p1.x) ** 2 + (a.y - p1.y) ** 2);
                const dist2 = Math.sqrt((b.x - p1.x) ** 2 + (b.y - p1.y) ** 2);
                const dist3 = Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);
                const tolerance = 0.0001;
                if (Math.abs(dist1 + dist2 - dist3) < tolerance) {
                    return closet.id;
                }
            }
        }
    }

    getDistance(p1, p2) {
        return Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
    }

    selectCloset(id) {
        if (id) {
            const selectedCloset = {
                id: id,
                coordinates: [],
            };
            this.setSelectedCloset(selectedCloset);
        } else {
            this.setSelectedCloset(null);
        }
    }

    setSelectedCloset(closet) {
        this._selectedCloset = closet;
        if (closet) {
            this.setDistanceLabel(false);
        } else {
            if (!this._libState.drawingManager.closetPolygon.length > 0)
                this.setDistanceLabel(true);
            this.drawNewClosets();
        }
    }

    setSelectedClosetById(id) {
        const closet = this._closets.find((c) => c.id === id);
        this.setSelectedCloset(closet);
    }

    setSelectedUnitById(id) {
        const unit = this._units.find((u) => u.id === id);
        this.setSelectedUnit(unit);
    }

    addCloset(id, closetPolygon) {
        const closet = new Closet(
            id,
            this.selectedPageNo,
            this.getScaleForPage(this.selectedPageNo),
        );
        closet.addCoordinate(closetPolygon);
        this._closets.push(closet);
        this._closets = [...this._closets];
        this.closetInUnitValidation();
        this.drawNewClosets();
    }

    updateUnitQtyByName(name, qty) {
        const unit = this._units.find((unit) => unit.name === name);
        if (unit) {
            unit._qty = qty;
            this._units = [...this._units];
        }
    }

    updateUnitNameByName(name, newName) {
        const unit = this._units.find((unit) => unit.name === name);
        if (unit) {
            unit._name = newName;
            this._units = [...this._units];
        }
    }

    updateClosetNameById(id, newName) {
        const closet = this._closets.find((c) => c.id === id);
        if (closet) {
            closet._name = newName;
            this._closets = [...this._closets];
        }
        this.setSelectedCloset(null);
    }

    updateUnitPagesByName(name, page, checked) {
        const unit = this.units.find((unit) => unit.name === name);
        if (unit) {
            const pages = unit.pages;
            if (checked) {
                if (!pages.includes(page)) {
                    pages.push(page);
                }
            } else {
                const index = pages.indexOf(page);
                if (index > -1) {
                    pages.splice(index, 1);
                }
            }
            unit.setPages(pages);
        }
    }

    checkIsOutsideUnit() {
        if (!this._selectedCloset) return;
        const isClosetPresent = this.closets.some((closet) => {
            return closet.id === this._selectedCloset.id;
        });
        const isUnitPresent = this.units.some((unit) => {
            return unit.id === this._selectedCloset.id;
        });
        if (!isClosetPresent && !isUnitPresent) return;
        if (isClosetPresent) {
            this.closets.forEach((closet) => {
                const closetPolygon = closet.coordinates;
                let isOutside = true;
                let isIntersecting = false;
                isIntersecting = Utils.isSelfIntersect(closetPolygon);
                if (!isIntersecting) {
                    isIntersecting = this.closetToClosetIntersection(
                        this.selectedCloset,
                    );
                    if (isIntersecting) {
                        message.error('Closet intersects with another closet.');
                    }
                }
                if (!isIntersecting) {
                    this.units.forEach((unit) => {
                        if (unit.pageNo === closet.pageNo && isOutside) {
                            if (unit.isClosetInsideUnit(closetPolygon)) {
                                isOutside = false;
                                closet.setUnitId(unit.id);
                            }
                        }
                    });
                }
                if (isOutside || isIntersecting) {
                    const index = this.closets.findIndex(
                        (c) => c.id === this.selectedCloset.id,
                    );
                    if (index !== -1) {
                        this.closets[index].setAllCoordinates(
                            this.initialSelectedCoords,
                        );
                        this.drawNewClosets();
                    }
                }
            });
        } else if (isUnitPresent) {
            const unitCoordinates = this._selectedCloset.coordinates;
            let isIntersecting = Utils.isSelfIntersect(unitCoordinates);
            if (!isIntersecting) {
                isIntersecting = this.unitToUnitIntersection(
                    this.selectedCloset,
                );
                if (isIntersecting) {
                    message.error('Unit intersects with another unit .');
                }
            }
            if (isIntersecting) {
                const index = this.units.findIndex(
                    (u) => u.id === this.selectedCloset.id,
                );
                if (index !== -1) {
                    this.units[index].setAllCoordinates(
                        this.initialSelectedCoords,
                    );
                    this.drawNewClosets();
                }
            }
        }
    }

    closetInUnitValidation() {
        this.closets.forEach((closet) => {
            const closetPolygon = closet.coordinates;
            let isOutside = true;
            this.units.forEach((unit) => {
                if (unit.pageNo === closet.pageNo && isOutside) {
                    if (unit.isClosetInsideUnit(closetPolygon)) {
                        isOutside = false;
                        closet.setUnitId(unit.id);
                    }
                }
            });
            if (isOutside) {
                closet.setUnitId(null);
            }
        });
        this.closets.forEach((closet) => {
            if (!closet.unitId) {
                const isClosetSelected = this.selectedCloset?.id === closet.id;
                this.removeClosetById(closet.id);
                if (isClosetSelected) this.setSelectedCloset(null);
            }
        });
        this.obstacles.forEach((obstacle) => {
            let isInside = false;
            this.closets.forEach((closet) => {
                const closetPolygon = closet.coordinates;
                if (
                    Utils.pointInPolygon(
                        obstacle.data.coordinate,
                        closetPolygon,
                    )
                ) {
                    isInside = true;
                }
            });
            if (!isInside) {
                this.removeObstacleById(obstacle.id);
                message.error('Obstacle is not in any closet.');
            }
        });
        this.internalWalls.forEach((wall) => {
            let isInside = false;
            this.closets.forEach((closet) => {
                const closetPolygon = closet.coordinates;
                if (Utils.pointInPolygon(wall.coordinate, closetPolygon)) {
                    isInside = true;
                }
            });
            if (!isInside) {
                this.removeInternalWallById(wall.id);
                message.error('Internal wall is not in any closet.');
            }
        });
    }

    addUnit(id, closetPolygon) {
        const unit = new Unit(
            id,
            this.selectedPageNo,
            this.generateUnitName(),
            this.getScaleForPage(this.selectedPageNo),
        );
        unit.addCoordinate(closetPolygon);
        this._units.push(unit);
        this._units = [...this._units];
        this.closetInUnitValidation();
        this.drawNewClosets();
        this.drawSnapPoints();
    }
    addExistingClosets(id, closetPolygon, pageNum, unitId, name, scale) {
        const closet = new Closet(id, pageNum, scale);
        closet.addCoordinate(closetPolygon);
        closet.setUnitId(unitId);
        closet.setName(name);
        this._closets.push(closet);
        this._closets = [...this._closets];
    }

    addExistingUnits(id, closetPolygon, pageNum, unitName, scale) {
        const unit = new Unit(id, pageNum, unitName, scale);
        unit.addCoordinate(closetPolygon);
        this.closetInUnitValidation();
        this._units.push(unit);
        this._units = [...this._units];
    }

    generateUnitName() {
        const existingNames = this._units.map((unit) => unit.name);
        const baseName = 'Unit-';
        let count = 1;
        let unitName = baseName + count;
        while (existingNames.includes(unitName)) {
            count += 1;
            unitName = baseName + count;
        }
        return unitName;
    }

    addAttachment(id, closetId, attachmentPolygon) {
        const attachment = new Attachment(id, closetId, this.selectedPageNo);
        attachment.addCoordinate(attachmentPolygon);
        this._attachments.push(attachment);
        this._attachments = [...this._attachments];
        this.drawNewClosets();
        this.drawSnapPoints();
    }
    addInternalWall(id) {
        const wall = new InternalWall(
            id,
            this.internalWallData.closetId,
            this.selectedPageNo,
            this.getScaleUnitByClosetId(this.internalWallData.closetId),
            this.internalWallData.width,
            this.internalWallData.height,
            this.internalWallData.angle || 0,
            this.internalWallData.position || 0,
            this.internalWallData.wallNormal,
        );
        this.internalWalls.push(wall);
        this.setInternalWallData(wall);
        this.drawNewClosets();
        this.drawSnapPoints();
    }
    updateSelectedInternalWall(wallData) {
        const wall = this.internalWalls.find(
            (wall) => wall.id === this.internalWallData.id,
        );
        if (!wall) return false;
        const scaleFactor = this.getScaleFactorForAnnotationDisplayByClosetId(
            this.selectedPageNo,
            wall.closetId,
            true,
        );
        wall.updateLengthInDirection(
            wallData.length * scaleFactor * SCALEFACTOR_FOR_CONVERSION,
        );
        wall.setDepth(
            wallData.depth * scaleFactor * SCALEFACTOR_FOR_CONVERSION,
        );
        wall.setRotation(wallData.rotation);
        this.drawNewClosets();
        this.drawSnapPoints();
        return true;
    }

    addCalibrationLine(callibrationLine) {
        this.setCalibrationData(callibrationLine);
        this.drawNewClosets();
    }

    addExistingAttachments(id, closetId, attachmentPolygon, pageNum) {
        const attachment = new Attachment(id, closetId, pageNum);
        attachment.addCoordinate(attachmentPolygon);
        this._attachments.push(attachment);
    }

    drawNewClosets = () => {
        this.clearClosetCanvas();
        this.closetCanvas.width = this.pdfCanvasRef.width;
        this.closetCanvas.height = this.pdfCanvasRef.height;
        const selectedPage = this.selectedPageNo;
        const context = this.closetCanvas.getContext('2d');
        context.translate(this.translateX, this.translateY);

        // Draw closets
        this.closets.forEach((closet) => {
            if (closet.pageNo !== selectedPage) return;

            const coords = closet.coordinates;

            // Outline & corner points
            ClosetUtils.drawPolygon(context, coords, 'red');

            // Edge distance labels
            if (this.distanceLabel) {
                const scale = this.getScaleFactorForAnnotationDisplayByClosetId(
                    this.selectedPageNo,
                    closet.id,
                );
                ClosetUtils.drawEdgeLabels(
                    context,
                    coords,
                    scale,
                    CanvasUtils.drawDistanceLabel,
                );
            }

            // Mid-points (only when no attachments/obstacles are present)
            const hasDependency =
                this.attachments.some((a) => a.closetId === closet.id) ||
                this.obstacles.some((o) => o.closetId === closet.id);

            if (!hasDependency) {
                ClosetUtils.drawMidPoints(context, coords, plusIconData);
            }
        });

        // Draw units
        this.units.forEach((unit) => {
            if (unit.pageNo !== selectedPage) return;

            const coords = unit.coordinates;

            ClosetUtils.drawPolygon(context, coords, 'orange');

            if (this.distanceLabel) {
                const scale = this.getScaleFactorForAnnotationDisplayByUnitId(
                    this.selectedPageNo,
                    unit.id,
                );
                ClosetUtils.drawEdgeLabels(
                    context,
                    coords,
                    scale,
                    CanvasUtils.drawDistanceLabel,
                );
            }

            const hasCloset = this.closets.some(
                (closet) => closet.unitId === unit.id,
            );
            if (!hasCloset) {
                ClosetUtils.drawMidPoints(context, coords, plusIconData);
            }
        });

        if (this.canCalibrate && this.calibrationData.length > 0) {
            CanvasUtils.drawLines(context, this.calibrationData, 'green');
            this.calibrationData.forEach((coordinate) => {
                CanvasUtils.drawCircle(context, coordinate, 5, 'green');
            });
        }

        this.attachments.forEach((attachment) => {
            if (attachment.pageNo !== selectedPage) return;

            const polygonCoordinate = attachment.coordinates;
            const closet = this.closets.find(
                (c) => c.id === attachment.closetId,
            );
            const closetPolygon = closet.coordinates;
            if (attachment.attachmentType === AttachmentType.DOOR) {
                CanvasUtils.drawDoorSwing(
                    context,
                    polygonCoordinate[0],
                    polygonCoordinate[1],
                    attachment._opening,
                    closetPolygon,
                    'green',
                    attachment.type,
                    attachment.swing,
                );
            } else {
                CanvasUtils.drawLines(context, polygonCoordinate, 'blue', 7);
            }
            polygonCoordinate.forEach((coordinate, index) => {
                CanvasUtils.drawCircle(context, coordinate, 5, 'red');
                if (index > 0) {
                    const scaleFactor =
                        this.getScaleFactorForAnnotationDisplayByClosetId(
                            this.selectedPageNo,
                            closet.id,
                        );
                    if (this.distanceLabel) {
                        CanvasUtils.drawDistanceLabelForAttachments(
                            context,
                            polygonCoordinate[index - 1],
                            coordinate,
                            scaleFactor,
                        );
                    }
                }
            });

            attachment.setClosetId(
                this.fetchClosetIdByAttachmentCoordinates(
                    attachment.coordinates,
                    selectedPage,
                ),
            );
        });

        this.obstacles.forEach((obstacle) => {
            let color = '';
            if (
                this.selectedObstacle &&
                this.selectedObstacle.id === obstacle.id
            ) {
                color = 'blue';
            }
            if (obstacle.pageNo === selectedPage) {
                if (!obstacle.data.radius) {
                    CanvasUtils.drawRectangleForObstacles(
                        context,
                        obstacle.data.coordinate,
                        obstacle.data.width,
                        obstacle.data.height,
                        obstacle.data.rotation || 0,
                        color === 'blue' ? 'blue' : 'green',
                        color === 'blue' ? 0.75 : 0.5,
                    );
                } else {
                    CanvasUtils.drawCircle(
                        context,
                        obstacle.data.coordinate,
                        obstacle.data.radius,
                        color === 'blue' ? 'blue' : 'red',
                        color === 'blue' ? 0.75 : 0.5,
                    );
                }
                this.drawPositionLines(obstacle.id);
            }
        });
        this.internalWalls.forEach((wall) => {
            let color = '';
            if (this.internalWallData && this.internalWallData.id === wall.id) {
                color = 'blue';
            }
            if (wall.pageNo === selectedPage) {
                CanvasUtils.drawRectangleForObstacles(
                    context,
                    wall.coordinate,
                    wall.length,
                    wall.depth,
                    wall.rotation || 0,
                    color === 'blue' ? 'blue' : 'purple',
                    color === 'blue' ? 0.75 : 0.5,
                );
                this.drawPositionLines(wall.id, true);
            }
        });
        if (!this.firstLoading) {
            this.drawSnapPoints();
        }
    };

    addMinusButton = () => {
        if (this._libState.viewManager.buttonMode === ButtonMode.Closet) {
            const selectedPage = this.selectedPageNo;
            const context = this.closetCanvas.getContext('2d');
            this.closets.forEach((closet) => {
                if (closet.pageNo !== selectedPage) return;

                const polygonCoordinate = closet.coordinates;
                polygonCoordinate.forEach((coordinate) => {
                    context.drawImage(
                        minusIconData,
                        coordinate.x - 10,
                        coordinate.y - 10,
                        20,
                        20,
                    );
                });
            });
        } else if (this._libState.viewManager.buttonMode === ButtonMode.Door) {
            const selectedPage = this.selectedPageNo;
            const context = this.closetCanvas.getContext('2d');
            this.attachments.forEach((attachment) => {
                if (attachment.pageNo !== selectedPage) return;

                const coordinateX =
                    attachment.coordinates[0].x + attachment.coordinates[1].x;
                const coordinateY =
                    attachment.coordinates[0].y + attachment.coordinates[1].y;
                context.drawImage(
                    minusIconData,
                    coordinateX / 2 - 10,
                    coordinateY / 2 - 10,
                    20,
                    20,
                );
            });
            this.obstacles.forEach((obstacle) => {
                if (obstacle.pageNo !== selectedPage) return;

                context.drawImage(
                    minusIconData,
                    obstacle.data.coordinate.x - 10,
                    obstacle.data.coordinate.y - 10,
                    20,
                    20,
                );
            });
            this.internalWalls.forEach((wall) => {
                if (wall.pageNo !== selectedPage) return;

                context.drawImage(
                    minusIconData,
                    wall.coordinate.x - 10,
                    wall.coordinate.y - 10,
                    20,
                    20,
                );
            });
        } else {
            const selectedPage = this.selectedPageNo;
            const context = this.closetCanvas.getContext('2d');
            this.units.forEach((unit) => {
                if (unit.pageNo !== selectedPage) return;

                const isClosetThere = this.closets.some(
                    (closet) => closet.unitId === unit.id,
                );
                if (!isClosetThere) {
                    const polygonCoordinate = unit.coordinates;
                    polygonCoordinate.forEach((coordinate) => {
                        context.drawImage(
                            minusIconData,
                            coordinate.x - 10,
                            coordinate.y - 10,
                            20,
                            20,
                        );
                    });
                }
            });
        }
    };

    addDeletePolygonButton = () => {
        if (this._libState.viewManager.buttonMode == ButtonMode.Closet) {
            const selectedPage = this.selectedPageNo;
            const context = this.closetCanvas.getContext('2d');
            this.closets.forEach((closet) => {
                if (closet.pageNo !== selectedPage) return;

                const polygonCoordinate = closet.coordinates;
                let meanX = 0;
                let meanY = 0;
                polygonCoordinate.forEach((coordinate) => {
                    meanX += coordinate.x;
                    meanY += coordinate.y;
                });

                context.drawImage(
                    minusIconData,
                    meanX / polygonCoordinate.length - 20,
                    meanY / polygonCoordinate.length - 20,
                    40,
                    40,
                );
            });
        } else if (this._libState.viewManager.buttonMode == ButtonMode.Unit) {
            const selectedPage = this.selectedPageNo;
            const context = this.closetCanvas.getContext('2d');
            this.units.forEach((unit) => {
                if (unit.pageNo !== selectedPage) return;

                const polygonCoordinate = unit.coordinates;
                let meanX = 0;
                let meanY = 0;
                polygonCoordinate.forEach((coordinate) => {
                    meanX += coordinate.x;
                    meanY += coordinate.y;
                });

                context.drawImage(
                    minusIconData,
                    meanX / polygonCoordinate.length - 20,
                    meanY / polygonCoordinate.length - 20,
                    40,
                    40,
                );
            });
        }
    };

    enableEditPolygon = (coordinate) => {
        let selectedCloset = null;
        const closetCanvasRect = this.closetCanvasRect;
        coordinate.x = coordinate.x - closetCanvasRect.left - this.translateX;
        coordinate.y = coordinate.y - closetCanvasRect.top - this.translateY;
        for (const attachment of this.attachments) {
            const coordinateFound = attachment.findAttachmentByCoordinate(
                coordinate,
                this.tolForDelete,
            );
            if (coordinateFound) {
                selectedCloset = attachment;
                break;
            }
        }
        if (!selectedCloset) {
            for (const closet of this.closets) {
                const coordinateFound = closet.findClosetByCoordinate(
                    coordinate,
                    this.tolForDelete,
                );
                if (coordinateFound) {
                    selectedCloset = closet;
                    this.setInitialSelectedCoords(
                        JSON.parse(JSON.stringify(closet.coordinates)),
                    );
                    break;
                }
            }
        }
        if (!selectedCloset) {
            for (const unit of this.units) {
                const coordinateFound = unit.findUnitByCoordinate(
                    coordinate,
                    this.tolForDelete,
                );
                if (coordinateFound) {
                    selectedCloset = unit;
                    this.setInitialSelectedCoords(
                        JSON.parse(JSON.stringify(unit.coordinates)),
                    );
                    break;
                }
            }
        }

        this.setSelectedCloset(selectedCloset);
    };

    enableEditObstacle = (coordinate) => {
        let selectedCloset = null;
        const closetCanvasRect = this.closetCanvasRect;
        coordinate.x = coordinate.x - closetCanvasRect.left - this.translateX;
        coordinate.y = coordinate.y - closetCanvasRect.top - this.translateY;
        for (const obstacle of this.obstacles) {
            const coordinateFound = obstacle.findObstacleByCoordinate(
                coordinate,
                this.tolForDelete,
            );
            if (coordinateFound) {
                selectedCloset = obstacle;
                break;
            }
        }
        if (selectedCloset) {
            this.setEnableEditObstacleForm(true);
            this.setSelectedObstacle(selectedCloset);
        }
        this.getTopAndLeft();
    };

    addMidPoint = (coordinate) => {
        const closetCanvasRect = this.closetCanvasRect;
        coordinate.x = coordinate.x - closetCanvasRect.left - this.translateX;
        coordinate.y = coordinate.y - closetCanvasRect.top - this.translateY;
        for (const closet of this.closets) {
            let isAttachmentThere = this.attachments.some(
                (attachment) => attachment.closetId === closet.id,
            );
            if (!isAttachmentThere) {
                isAttachmentThere = this.obstacles.some(
                    (obstacle) => obstacle.closetId === closet.id,
                );
            }

            if (isAttachmentThere) continue;

            const coordinateFound =
                closet.checkPointInMiddleOfAnyLine(coordinate);
            if (coordinateFound !== -1) {
                closet.addNewPointInLine(coordinateFound);
                this.drawNewClosets();
                break;
            }
        }
        for (const unit of this.units) {
            const isClosetThere = this.closets.some(
                (closet) => closet.unitId === unit.id,
            );

            if (isClosetThere) continue;

            const coordinateFound =
                unit.checkPointInMiddleOfAnyLine(coordinate);
            if (coordinateFound !== -1) {
                unit.addNewPointInLine(coordinateFound);
                this.drawNewClosets();
                break;
            }
        }
    };

    deletePolygonPoint = (coordinate) => {
        if (this._libState.viewManager.buttonMode == ButtonMode.Closet) {
            const closetCanvasRect = this.closetCanvasRect;
            coordinate.x =
                coordinate.x - closetCanvasRect.left - this.translateX;
            coordinate.y =
                coordinate.y - closetCanvasRect.top - this.translateY;
            for (const closet of this.closets) {
                const coordinateFound = closet.findClosetByCoordinate(
                    coordinate,
                    this.tolForDelete,
                );
                if (coordinateFound) {
                    this.setSelectedCloset(closet);
                    this.setInitialSelectedCoords(
                        JSON.parse(JSON.stringify(closet.coordinates)),
                    );
                    for (const attachment of this.attachments) {
                        if (attachment.closetId !== closet.id) {
                            continue;
                        }
                        const [start, end] = attachment.coordinates;
                        if (
                            Utils.isPointCollinear(coordinateFound, start, end)
                        ) {
                            this.removeAttachmentById(attachment.id);
                        }
                    }
                    closet.deletePointByCoordinate(coordinateFound);
                    this.checkIsOutsideUnit();
                    if (
                        closet.coordinates.length ===
                        this.initialSelectedCoords.length
                    )
                        message.info(
                            'No changes made as it will cause self intersecting Closet or closet out of unit.',
                        );
                    this.setInitialSelectedCoords(null);
                    this.setSelectedCloset(null);
                    this.closetValidation();
                    this.drawNewClosets();
                    this.setDeleteMode();
                    return true;
                }
            }
            return false;
        } else if (this._libState.viewManager.buttonMode == ButtonMode.Unit) {
            const closetCanvasRect = this.closetCanvasRect;
            coordinate.x =
                coordinate.x - closetCanvasRect.left - this.translateX;
            coordinate.y =
                coordinate.y - closetCanvasRect.top - this.translateY;
            for (const unit of this.units) {
                const coordinateFound = unit.findUnitByCoordinate(
                    coordinate,
                    this.tolForDelete,
                );
                const isClosetThere = this.closets.some(
                    (closet) => closet.unitId === unit.id,
                );
                if (isClosetThere) continue;
                if (coordinateFound) {
                    this.setSelectedCloset(unit);
                    this.setInitialSelectedCoords(
                        JSON.parse(JSON.stringify(unit.coordinates)),
                    );
                    unit.deletePointByCoordinate(coordinateFound);
                    this.checkIsOutsideUnit();
                    if (
                        unit.coordinates.length ===
                        this.initialSelectedCoords.length
                    )
                        message.info(
                            'No changes made as it will cause self intersecting Closet or closet out of unit.',
                        );
                    this.setInitialSelectedCoords(null);
                    this.setSelectedCloset(null);
                    this.unitValidation();
                    this.drawNewClosets();
                    this.setDeleteMode();
                    return true;
                }
            }
            return false;
        }
    };

    closetValidation = () => {
        this.closets.forEach((closet) => {
            if (closet.coordinates.length < 3) {
                this.removeClosetById(closet.id);
            }
        });
        this.closetInUnitValidation();
    };

    closetToClosetIntersection = (selectedCloset) => {
        let isIntersecting = false;

        this.closets.forEach((closet) => {
            if (
                closet.id !== selectedCloset.id &&
                closet.pageNo === selectedCloset.pageNo
            ) {
                const closetPolygon = selectedCloset.coordinates;
                if (closet.isClosetIntersecting(closetPolygon, 0)) {
                    isIntersecting = true;
                    return isIntersecting;
                }
            }
        });
        return isIntersecting;
    };

    closetIntersectionForIncompleteClosets = (closetPolygon, index = 1) => {
        let isIntersecting = false;
        this.closets.forEach((closet) => {
            if (closet.pageNo !== this.selectedPageNo) return;
            if (closet.isClosetIntersecting(closetPolygon, index)) {
                isIntersecting = true;
                return isIntersecting;
            }
        });
        return isIntersecting;
    };

    unitIntersectionForIncompleteUnits = (unitPolygon, index) => {
        let isIntersecting = false;
        this.units.forEach((unit) => {
            if (unit.pageNo !== this.selectedPageNo) return;
            if (unit.isUnitIntersecting(unitPolygon, index)) {
                isIntersecting = true;
                return isIntersecting;
            }
        });
        return isIntersecting;
    };

    unitToUnitIntersection = (selectedUnit) => {
        let isIntersecting = false;
        this.units.forEach((unit) => {
            if (
                unit.id !== selectedUnit.id &&
                unit.pageNo === selectedUnit.pageNo
            ) {
                const unitPolygon = selectedUnit.coordinates;
                if (unit.isUnitIntersecting(unitPolygon, 0)) {
                    isIntersecting = true;
                    return isIntersecting;
                }
            }
        });
        return isIntersecting;
    };

    unitValidation = () => {
        this.units.forEach((unit) => {
            if (unit.coordinates.length < 3) {
                this.removeUnitById(unit.id);
            }
        });
        this.closetInUnitValidation();
    };

    updateSelectedClosetPolygon = (coordinate) => {
        if (typeof this._lastEditedPointIndex === 'undefined') {
            this._lastEditedPointIndex = null;
        }

        const closetCanvasRect = this.closetCanvasRect;
        coordinate.x = coordinate.x - closetCanvasRect.left - this.translateX;
        coordinate.y = coordinate.y - closetCanvasRect.top - this.translateY;
        const isClosetPresent = this.closets.some((closet) => {
            return closet.id === this._selectedCloset.id;
        });
        const isUnitPresent = this.units.some((unit) => {
            return unit.id === this._selectedCloset.id;
        });
        if (isUnitPresent) {
            const isClosetPresent = this.closets.some((closet) => {
                return closet.unitId === this._selectedCloset.id;
            });
            if (isClosetPresent) return;
            const coordinates = this._selectedCloset.coordinates;
            let coordinateNeedToUpdate = null;

            let targetIndex = this.lastEditedPointIndex;
            if (targetIndex === null) {
                const closestCoord = this._selectedCloset.closestCoordinate(
                    coordinate,
                    this.tolForDelete,
                );
                if (closestCoord) {
                    targetIndex = coordinates.findIndex(
                        (coord) =>
                            coord.x === closestCoord.x &&
                            coord.y === closestCoord.y,
                    );
                    if (targetIndex !== -1) {
                        this.setLastEditedPointIndex(targetIndex);
                    }
                }
            }

            if (targetIndex !== -1) {
                coordinateNeedToUpdate = coordinates[targetIndex];
            }

            if (coordinateNeedToUpdate) {
                const nearestPoint =
                    this._libState.drawingManager.findNearestPoint(
                        coordinate,
                        this._libState.drawingManager.snapThreshold,
                    );
                if (
                    !nearestPoint ||
                    nearestPoint.x == null ||
                    nearestPoint.y == null
                ) {
                    coordinateNeedToUpdate.x = coordinate.x;
                    coordinateNeedToUpdate.y = coordinate.y;
                } else {
                    coordinateNeedToUpdate.x = nearestPoint.x;
                    coordinateNeedToUpdate.y = nearestPoint.y;
                }
            }
            this.drawNewClosets();
            this.drawSnapPoints();
            return;
        }
        if (isClosetPresent) {
            const nearestPoint = this._libState.drawingManager.findNearestPoint(
                coordinate,
                this._libState.drawingManager.snapThreshold,
            );
            const isAttachmentPresent = this.attachments.some((attachment) => {
                return attachment.closetId === this._selectedCloset.id;
            });
            if (isAttachmentPresent) return;
            const coordinates = this._selectedCloset.coordinates;
            let coordinateNeedToUpdate = null;
            let targetIndex = this.lastEditedPointIndex;
            if (targetIndex === null) {
                const closestCoord = this._selectedCloset.closestCoordinate(
                    coordinate,
                    this.tolForDelete,
                );
                if (closestCoord) {
                    targetIndex = coordinates.findIndex(
                        (coord) =>
                            coord.x === closestCoord.x &&
                            coord.y === closestCoord.y,
                    );
                    if (targetIndex !== -1) {
                        this.setLastEditedPointIndex(targetIndex);
                    }
                }
            }

            if (targetIndex !== -1) {
                coordinateNeedToUpdate = coordinates[targetIndex];
            }

            if (coordinateNeedToUpdate) {
                if (
                    !nearestPoint ||
                    nearestPoint.x == null ||
                    nearestPoint.y == null
                ) {
                    coordinateNeedToUpdate.x = coordinate.x;
                    coordinateNeedToUpdate.y = coordinate.y;
                } else {
                    coordinateNeedToUpdate.x = nearestPoint.x;
                    coordinateNeedToUpdate.y = nearestPoint.y;
                }
            }
            this.drawNewClosets();
            this.drawSnapPoints();
        } else {
            const attachment = this.attachments.find((attachment) => {
                return attachment.id === this._selectedCloset.id;
            });

            const allCoords = attachment.coordinates;
            const coordInTol = allCoords.reduce(
                (nearest, coord) => {
                    const dist = Math.hypot(
                        coord.x - coordinate.x,
                        coord.y - coordinate.y,
                    );
                    return dist < this.tolForDelete && dist < nearest.dist
                        ? { coord, dist }
                        : nearest;
                },
                { coord: null, dist: Infinity },
            ).coord;
            if (
                this.doorIntersectionCheckForExistingAttachment(
                    coordInTol,
                    attachment,
                )
            ) {
                message.error('Door intersects with another door.');
                return;
            }
            const coordNotInTol = allCoords.find((coord) => {
                return !(
                    Math.abs(coord.x - coordinate.x) < this.tolForDelete &&
                    Math.abs(coord.y - coordinate.y) < this.tolForDelete
                );
            });
            const coordinateNeedToUpdate =
                this._selectedCloset.closestAttachmentCoordinate(
                    coordinate,
                    this.tolForDelete,
                );
            const attachmentClosetId = this._selectedCloset.closetId;
            const attachmentCloset = this.closets.find((closet) => {
                return closet.id === attachmentClosetId;
            });
            const closetEdge = attachmentCloset.getClosetEdgeByCoordinate(
                coordinate,
                this.tolForDelete,
            );
            const closetEdge2 = attachmentCloset.getClosetEdgeByCoordinate(
                coordNotInTol,
                this.tolForDelete,
            );
            if (
                !Utils.isPointOnLine(
                    closetEdge?.[0],
                    closetEdge2?.[0],
                    closetEdge2?.[1],
                ) ||
                !Utils.isPointOnLine(
                    closetEdge?.[1],
                    closetEdge2?.[0],
                    closetEdge2?.[1],
                )
            ) {
                message.error('Move the point on the same edge.');
                return;
            }
            const attachmentEdge = attachmentCloset.getClosetEdgeByCoordinate(
                coordinateNeedToUpdate == this.selectedCloset.coordinates[0]
                    ? this.selectedCloset.coordinates[1]
                    : this.selectedCloset.coordinates[0],
                this.tolForDelete,
            );
            if (
                (closetEdge?.[0]?.x !== attachmentEdge?.[0]?.x &&
                    closetEdge?.[0]?.y !== attachmentEdge?.[0]?.y) ||
                (closetEdge?.[1]?.x !== attachmentEdge?.[1]?.x &&
                    closetEdge?.[1]?.y !== attachmentEdge?.[1]?.y)
            )
                return;
            if (coordinateNeedToUpdate) {
                const nearestPoint = this.nearestClosetPoint(
                    coordinate,
                    this.tolForDelete,
                    false,
                );
                if (
                    !nearestPoint ||
                    nearestPoint.x == null ||
                    nearestPoint.y == null ||
                    !Utils.isPointOnLine(
                        nearestPoint,
                        closetEdge[0],
                        closetEdge[1],
                    )
                ) {
                    return;
                } else {
                    coordinateNeedToUpdate.x = nearestPoint.x;
                    coordinateNeedToUpdate.y = nearestPoint.y;
                }
            }
            this.drawNewClosets();
            this.drawSnapPoints();
        }
    };

    checkClosetInsideUnit = (closetPolygon, liveLine) => {
        let isInside = false;
        for (const unit of this.units) {
            if (unit.pageNo !== this.selectedPageNo) continue;
            if (unit.isClosetInsideUnit(closetPolygon, liveLine)) {
                isInside = true;
                break;
            }
        }
        return isInside;
    };

    drawAllClosets = () => {
        this.clearClosetCanvas();
        this.closetCanvas.width = this.pdfCanvasRef.width;
        this.closetCanvas.height = this.pdfCanvasRef.height;
        const selectedPage = this.selectedPageNo;
        const context = this.closetCanvas.getContext('2d');
        context.translate(this.translateX, this.translateY);
        this.closets.forEach((closet) => {
            if (closet.pageNo !== selectedPage) return;

            const coords = closet.coordinates;
            // scale in-place for current zoom
            coords.forEach((c) => {
                c.x = c.x / this.scaleForZoom;
                c.y = c.y / this.scaleForZoom;
            });

            ClosetUtils.drawPolygon(context, coords, 'red');

            if (this.distanceLabel) {
                const scale = this.getScaleFactorForAnnotationDisplayByClosetId(
                    this.selectedPageNo,
                    closet.id,
                );
                ClosetUtils.drawEdgeLabels(
                    context,
                    coords,
                    scale,
                    CanvasUtils.drawDistanceLabel,
                );
            }

            const hasDependency =
                this.attachments.some((a) => a.closetId === closet.id) ||
                this.obstacles.some((o) => o.closetId === closet.id);

            if (!hasDependency) {
                ClosetUtils.drawMidPoints(context, coords, plusIconData);
            }
        });
        this.units.forEach((unit) => {
            if (unit.pageNo !== selectedPage) return;

            const coords = unit.coordinates;
            coords.forEach((c) => {
                c.x = c.x / this.scaleForZoom;
                c.y = c.y / this.scaleForZoom;
            });

            ClosetUtils.drawPolygon(context, coords, 'orange');

            if (this.distanceLabel) {
                const scale = this.getScaleFactorForAnnotationDisplayByUnitId(
                    this.selectedPageNo,
                    unit.id,
                );
                ClosetUtils.drawEdgeLabels(
                    context,
                    coords,
                    scale,
                    CanvasUtils.drawDistanceLabel,
                );
            }

            const hasCloset = this.closets.some(
                (closet) => closet.unitId === unit.id,
            );
            if (!hasCloset) {
                ClosetUtils.drawMidPoints(context, coords, plusIconData);
            }
        });

        if (this.canCalibrate && this.calibrationData.length > 0) {
            this.calibrationData.forEach((coordinate) => {
                coordinate.x = coordinate.x / this._scaleForZoom;
                coordinate.y = coordinate.y / this._scaleForZoom;
                CanvasUtils.drawCircle(context, coordinate, 5, 'green');
            });
            CanvasUtils.drawLines(context, this.calibrationData, 'green');
        }
        this.attachments.forEach((attachment) => {
            if (attachment.pageNo === selectedPage) {
                const polygonCoordinate = attachment.coordinates;
                const closet = this.closets.find(
                    (c) => c.id === attachment.closetId,
                );
                polygonCoordinate.forEach((coordinate, index) => {
                    if (index < polygonCoordinate.length) {
                        coordinate.x = coordinate.x / this._scaleForZoom;
                        coordinate.y = coordinate.y / this._scaleForZoom;
                    }
                });
                const closetPolygon = closet.coordinates;
                if (attachment.attachmentType === AttachmentType.DOOR) {
                    CanvasUtils.drawDoorSwing(
                        context,
                        polygonCoordinate[0],
                        polygonCoordinate[1],
                        attachment._opening,
                        closetPolygon,
                        'green',
                        attachment.type,
                        attachment.swing,
                    );
                } else {
                    CanvasUtils.drawLines(
                        context,
                        polygonCoordinate,
                        'blue',
                        7,
                    );
                }
                polygonCoordinate.forEach((coordinate, index) => {
                    CanvasUtils.drawCircle(context, coordinate, 5, 'red');
                    if (index > 0) {
                        if (this.distanceLabel) {
                            CanvasUtils.drawDistanceLabelForAttachments(
                                context,
                                polygonCoordinate[index - 1],
                                coordinate,
                                this.getScaleFactorForAnnotationDisplayByClosetId(
                                    this.selectedPageNo,
                                    attachment.closetId,
                                ),
                            );
                        }
                    }
                });
            }
        });
        this.obstacles.forEach((obstacle) => {
            if (obstacle.pageNo === selectedPage) {
                obstacle.data.coordinate.x /= this.scaleForZoom;
                obstacle.data.coordinate.y /= this.scaleForZoom;
                let color = '';
                if (
                    this.selectedObstacle &&
                    this.selectedObstacle.id === obstacle.id
                ) {
                    color = 'blue';
                }
                if (!obstacle.data.radius) {
                    obstacle.data.width /= this.scaleForZoom;
                    obstacle.data.height /= this.scaleForZoom;
                    CanvasUtils.drawRectangleForObstacles(
                        context,
                        obstacle.data.coordinate,
                        obstacle.data.width,
                        obstacle.data.height,
                        obstacle.data.rotation || 0,
                        color === 'blue' ? 'blue' : 'green',
                        color === 'blue' ? 0.75 : 0.5,
                    );
                } else {
                    obstacle.data.radius /= this.scaleForZoom;
                    CanvasUtils.drawCircle(
                        context,
                        obstacle.data.coordinate,
                        obstacle.data.radius,
                        color === 'blue' ? 'blue' : 'red',
                        color === 'blue' ? 0.75 : 0.5,
                    );
                }
                this.drawPositionLines(obstacle.id);
            }
        });
        this.internalWalls.forEach((wall) => {
            if (wall.pageNo === selectedPage) {
                const wallCoordinate = wall.coordinate;
                wallCoordinate.x /= this.scaleForZoom;
                wallCoordinate.y /= this.scaleForZoom;
                wall.setCoordinate(wallCoordinate);
                wall.setLength(wall.length / this.scaleForZoom);
                wall.setDepth(wall.depth / this.scaleForZoom);
                let color = '';
                if (
                    this.internalWallData &&
                    this.internalWallData.id === wall.id
                ) {
                    color = 'blue';
                }
                CanvasUtils.drawRectangleForObstacles(
                    context,
                    wall.coordinate,
                    wall.length,
                    wall.depth,
                    wall.rotation || 0,
                    color === 'blue' ? 'blue' : 'purple',
                    color === 'blue' ? 0.75 : 0.5,
                );
                this.drawPositionLines(wall.id, true);
            }
        });
    };

    isObstacleNear(coordinate) {
        const closetCanvasRect = this.closetCanvasRect;
        coordinate.x = coordinate.x - closetCanvasRect.left - this.translateX;
        coordinate.y = coordinate.y - closetCanvasRect.top - this.translateY;
        const scale =
            this.getScaleForPage(this.selectedPageNo) *
            this.fetchTransformationScaleFactorByPageNo(this.selectedPageNo);
        for (const obstacle of this.obstacles) {
            if (obstacle.pageNo === this.selectedPageNo) {
                if (obstacle.isObstacleInTol(coordinate, scale)) {
                    this.setSelectedObstacle(obstacle);
                    return { obstacle, isObstacle: true };
                }
            }
        }
        for (const wall of this.internalWalls) {
            if (wall.pageNo === this.selectedPageNo) {
                if (wall.isInternalWallInTol(coordinate, scale)) {
                    this.setInternalWallData(wall);
                    return { obstacle: wall, isObstacle: false };
                }
            }
        }
        return null;
    }

    deleteClosetPolygon(coordinate) {
        let selectedIdToDelete = null;
        const closetButton =
            this._libState.viewManager.buttonMode == ButtonMode.Closet;
        const doorButton =
            this._libState.viewManager.buttonMode == ButtonMode.Door;
        const closetCanvasRect = this.closetCanvasRect;
        coordinate.x = coordinate.x - closetCanvasRect.left - this.translateX;
        coordinate.y = coordinate.y - closetCanvasRect.top - this.translateY;
        if (doorButton) {
            for (const attachment of this.attachments) {
                const isClosetInTol = this.isAttachmentNear(
                    attachment.id,
                    coordinate,
                );
                if (isClosetInTol) {
                    selectedIdToDelete = attachment.id;
                    this.removeAttachmentById(selectedIdToDelete);
                    this.setDeleteMode();
                    return;
                }
            }
            for (const obstacle of this.obstacles) {
                if (obstacle.pageNo === this.selectedPageNo) {
                    if (
                        obstacle.isObstacleInTol(coordinate, this.tolForDelete)
                    ) {
                        this.removeObstacleById(obstacle.id);
                        message.success('Obstacle deleted successfully.');
                        break;
                    }
                }
            }
            for (const internalWall of this.internalWalls) {
                const tolForDel = 5;
                if (internalWall.pageNo === this.selectedPageNo) {
                    if (
                        internalWall.isInternalWallInTol(coordinate, tolForDel)
                    ) {
                        this.removeInternalWallById(internalWall.id);
                        message.success('Internal wall deleted successfully.');
                        break;
                    }
                }
            }
        } else if (closetButton) {
            for (const closet of this.closets) {
                if (this.isClosetSelected(closet.id, coordinate)) {
                    this.removeClosetById(closet.id);
                    message.success('Closet deleted successfully.');
                    for (const attachment of this.attachments) {
                        if (attachment.closetId === closet.id) {
                            this.removeAttachmentById(attachment.id);
                        }
                    }
                    break;
                }
            }
        } else {
            for (const unit of this.units) {
                if (unit.pageNo === this.selectedPageNo) {
                    if (this.isUnitSelected(unit.id, coordinate)) {
                        this.removeUnitById(unit.id);
                        this.closetInUnitValidation();
                        break;
                    }
                }
            }
        }

        this.setDeleteMode();
        this.drawNewClosets();
    }

    removeClosetById = (id) => {
        this._closets = this._closets.filter((closet) => closet.id !== id);
        this._attachments = this._attachments.filter(
            (attachment) => attachment.closetId !== id,
        );
        this._obstacles = this._obstacles.filter(
            (obstacle) => obstacle.closetId !== id,
        );
        this._internalWalls = this._internalWalls.filter(
            (wall) => wall.closetId !== id,
        );
        this._closets = [...this._closets];
        this._attachments = [...this._attachments];
    };

    removeAttachmentById = (id) => {
        this._attachments = this._attachments.filter(
            (attachment) => attachment.id !== id,
        );
        this._attachments = [...this._attachments];
        this.drawNewClosets();
        message.success('Attachment deleted successfully.');
    };

    removeUnitById = (id) => {
        this._units = this._units.filter((unit) => unit.id !== id);
        this._units = [...this._units];
        this.closetInUnitValidation();
        this.drawNewClosets();
        message.success('Unit deleted successfully.');
    };

    removeObstacleById = (id) => {
        this._obstacles = this._obstacles.filter(
            (obstacle) => obstacle.id !== id,
        );
        this._obstacles = [...this._obstacles];
        this.setSelectedObstacle(null);
    };
    removeInternalWallById = (id) => {
        this._internalWalls = this._internalWalls.filter(
            (wall) => wall.id !== id,
        );
        this._internalWalls = [...this._internalWalls];
    };
    isClosetInTol(id, coordinate) {
        const closet = this._closets.find((closet) => closet.id === id);
        return !!closet && closet.isClosetInTol(coordinate, this.tolForDelete);
    }

    isClosetSelected(id, coordinate) {
        const closet = this._closets.find((closet) => closet.id === id);
        return (
            !!closet && closet.isPointInCenter(coordinate, this.tolForDelete)
        );
    }

    isUnitSelected(id, coordinate) {
        const unit = this._units.find((unit) => unit.id === id);
        return !!unit && unit.isPointInCenter(coordinate, this.tolForDelete);
    }

    isAttachmentNear(id, coordinate) {
        const attachment = this._attachments.find(
            (attachment) => attachment.id === id,
        );
        return (
            !!attachment &&
            attachment.isAttachmentInTol(coordinate, this.tolForDelete)
        );
    }

    clearClosetCanvas() {
        if (!this.closetCanvas) return;
        this.closetCanvas
            .getContext('2d')
            .clearRect(
                -this.closetCanvas.width,
                -this.closetCanvas.height,
                this.closetCanvas.width * 2,
                this.closetCanvas.height * 2,
            );
    }

    async renderPage() {
        if (!this.pdfDoc) {
            const pdfDoc = await Utils.readPdf(this.pdfBuffer);
            this.setPdfDoc(pdfDoc);
        }

        // Ensure the PDF buffer exists
        if (!this.pdfBuffer) throw new Error('No PDF file selected.');

        // Get the selected page number
        const pageNo = this.selectedPageNo;
        const page = await this.pdfDoc.getPage(pageNo);
        const viewport = page.getViewport({
            scale: this.fetchTransformationScaleFactorByPageNo(pageNo),
        });
        // Resize the canvas to match the PDF page
        if (!this.pdfCanvasRef || !this.divRef) return;
        this.pdfCanvasRef.width = this.divRef.clientWidth;
        this.pdfCanvasRef.height = this.divRef.clientHeight;

        const ctx = this.pdfCanvasRef.getContext('2d');

        // **Cancel any ongoing rendering before starting a new one**
        if (this.renderTask) {
            await this.renderTask.cancel();
        }

        ctx.clearRect(0, 0, this.pdfCanvasRef.width, this.pdfCanvasRef.height);
        // Apply translation before drawing
        ctx.save();
        ctx.translate(this.translateX, this.translateY);
        // Render the PDF page
        this.renderTask = page.render({ canvasContext: ctx, viewport });
        try {
            await this.renderTask.promise;
            ctx.restore(); // Restore context
            this.extractTextAndExport(this.pdfDoc, pageNo);
        } catch (error) {
            if (error.name !== 'RenderingCancelledException') {
                if (!error.message.includes('multiple render operations')) {
                    console.error('Render failed:', error);
                }
            }
        }
    }

    extractTextAndExport = async (pdfDoc, PAGE_NO) => {
        try {
            const page = await pdfDoc.getPage(PAGE_NO);
            if (this.getScaleTypeForPage(PAGE_NO) !== ScaleSource.DEFAULT)
                return;
            const decimalValue = await Utils.extractTextFromCustomBox(
                EXTRACT_TEXT_WIDTH_PERCENTAGE,
                EXTRACT_TEXT_HEIGHT_PERCENTAGE,
                page,
            );
            if (decimalValue) {
                if (this.getScaleTypeForPage(PAGE_NO) === ScaleSource.DEFAULT) {
                    this.setPageScale(
                        PAGE_NO,
                        decimalValue,
                        ScaleSource.EXTRACTED,
                    );
                }
                return;
            }
            if (!this.getScaleForPage(PAGE_NO)) {
                this.setPageScale(
                    PAGE_NO,
                    DEFAULT_PAGE_SCALE,
                    ScaleSource.DEFAULT,
                );
            }
            console.warn('No fraction found in scale');
        } catch (error) {
            console.error('Error extracting text:', error);
        }
    };

    stopZoom = () => {
        this.setEnableZoom(!this.enableZoom);
    };
    setZoom(inBool) {
        this.setEnableZoom(inBool);
    }

    getMatrixCalculationData = async () => {
        let bool = this.isLoading;
        this.setIsLoading(true);
        if (!this._libState.viewManager.projectId) return;
        this.setIsLoading(bool);
    };

    loadSnappingPoints = async () => {
        const data = await this._libState.viewManager.snappingPoints;
        if (!data) return;
        // Extract coordinates for the selected page
        let selectedPageData = data[this.selectedPageNo] || [];
        if (selectedPageData.length === 0) {
            const responseData =
                await this._libState.apiManager.getCurrentProjectIdData();
            const selectedPageBinUrl =
                responseData.data.bin_files[`${this.selectedPageNo}_page`];
            const binPoints = await Utils.getArrayFromBinFile(
                selectedPageBinUrl,
                this.selectedPageNo,
            );
            let arr = {};
            if (this._libState.viewManager.snappingPoints) {
                arr = this._libState.viewManager.snappingPoints;
            }
            arr[this.selectedPageNo] = [...binPoints[this.selectedPageNo]];
            this._libState.viewManager.setSnappingPoints(arr);
        }

        selectedPageData = data[this.selectedPageNo];

        const CHUNK_SIZE = 1000000;
        const coordinateObj = [];
        const totalChunks = Math.ceil(selectedPageData.length / CHUNK_SIZE);

        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
            const start = chunkIndex * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, selectedPageData.length);
            const chunk = selectedPageData.slice(start, end);

            this.processChunk(chunk, coordinateObj);
            await new Promise((resolve) => setTimeout(resolve, 0));
        }

        this._libState.drawingManager.setPointsToSnapFrom(coordinateObj);
        this._coordinatesToSave = coordinateObj;
        this.drawSnapPoints();
    };

    getProcessedSnappingPoints = async () => {
        if (!this._libState.viewManager.projectId) return;
        const token = await getIdToken();
        const response = await fetch(
            `${SERVER_BASE_URL}/project/${this._libState.viewManager.projectId}`,
            {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            },
        );
        const result = await response.text();
        const responseData = JSON.parse(result);

        const statusList = this._libState.viewManager.pageStatus;

        Object.keys(responseData.data.bin_files).forEach((key) => {
            const pageNum = key.split('_')[0];

            statusList.forEach((status) => {
                if (status.pageNo == pageNum) {
                    this._libState.viewManager.updatePageStatus(
                        status.pageNo,
                        true,
                    );
                }
            });
        });
    };

    processChunk(dataChunk, coordinateObj) {
        for (let i = 0; i < dataChunk.length; i += 2) {
            coordinateObj.push({
                x: dataChunk[i],
                y: dataChunk[i + 1],
            });
        }
    }

    drawSnapPoints() {
        const context = this.closetCanvas.getContext('2d');
        const scaleOnPage = this.fetchTransformationScaleFactorByPageNo(
            this.selectedPageNo,
        );
        if (this.showSnapPoints) {
            this._coordinatesToSave.forEach((coordinate) => {
                const newCoordinates = {
                    x: coordinate.x * scaleOnPage,
                    y: coordinate.y * scaleOnPage,
                };
                CanvasUtils.drawCircle(context, newCoordinates, 1.5, 'red');
            });
        }
    }

    get closetCss() {
        if (this.enableZoom) {
            return 'overflow-hidden';
        } else {
            return 'overflow-auto';
        }
    }
    nearestClosetPoint(point, tol, bool) {
        let nearestPoint = null;
        let minDist = Infinity;
        let edgePoints = [];
        const context = this.closetCanvas.getContext('2d');

        this.closets.forEach((closet) => {
            if (closet.pageNo !== this.selectedPageNo) return;
            const closetPolygon = closet.coordinates;

            for (let i = 0; i < closetPolygon.length; i++) {
                const a = closetPolygon[i % closetPolygon.length];
                const b = closetPolygon[(i + 1) % closetPolygon.length];

                const lineVec = { x: b.x - a.x, y: b.y - a.y };
                const pointVec = { x: point.x - a.x, y: point.y - a.y };

                const lineLengthSquared =
                    lineVec.x * lineVec.x + lineVec.y * lineVec.y;
                const dotProduct =
                    pointVec.x * lineVec.x + pointVec.y * lineVec.y;

                const t = dotProduct / lineLengthSquared;
                const clampedT = Math.max(0, Math.min(1, t));

                const nearestOnSegment = {
                    x: a.x + clampedT * lineVec.x,
                    y: a.y + clampedT * lineVec.y,
                };

                const dist = Math.hypot(
                    point.x - nearestOnSegment.x,
                    point.y - nearestOnSegment.y,
                );

                if (dist < minDist && dist < tol) {
                    edgePoints = [];
                    minDist = dist;
                    nearestPoint = nearestOnSegment;
                    edgePoints.push(a, b);
                }
            }
        });
        if (bool) {
            this.setSelectedEdgeEnds(edgePoints);
            // CanvasUtils.drawCircle(context, nearestPoint, 4, 'blue');
        }

        return nearestPoint;
    }

    isPointOnLine(newPoint, tol) {
        let nearestEdgeEnds = this.selectedEdgeEnds;
        if (nearestEdgeEnds.length < 2) return false;

        const [point1, point2] = nearestEdgeEnds;
        const distance = (p1, p2) =>
            Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
        const d1 = distance(point1, newPoint);
        const d2 = distance(point2, newPoint);
        const segmentLength = distance(point1, point2);
        return Math.abs(d1 + d2 - segmentLength) <= tol;
    }

    drawExistingUnits(units) {
        this._libState.viewManager.selectedPages.forEach((page) => {
            const pageNum = Number(page.pageNum);

            const unitEntry = units.find((unit) =>
                Object.keys(unit).includes(String(pageNum)),
            );
            if (!unitEntry) return;

            const pageUnits = unitEntry[pageNum];
            if (!Array.isArray(pageUnits)) return;

            pageUnits.forEach((unit) => {
                if (unit) {
                    // if (unit.scale) {
                    //     this.setScaleFactorOnPdfPage(unit.scale);
                    //     this.setUnitScale(unit.id, unit.scale);
                    //     if (this.getScaleForPage(pageNum) === null) {
                    //         this.setPageScale(
                    //             pageNum,
                    //             unit.scale,
                    //             ScaleSource.SAVED,
                    //         );
                    //     }
                    // }
                    this.addExistingUnits(
                        unit.id,
                        unit.coordinates,
                        pageNum,
                        unit.name,
                        unit.scale,
                    );
                }
            });
        });
    }

    drawExistingClosets(closetData) {
        this._libState.viewManager.selectedPages.forEach((page) => {
            const pageNum = Number(page.pageNum);

            const closetEntry = closetData.find((closet) =>
                Object.keys(closet).includes(String(pageNum)),
            );
            if (!closetEntry) return;

            const pageClosets = closetEntry[pageNum];
            if (!Array.isArray(pageClosets)) return;

            pageClosets.forEach((pageCloset) => {
                if (pageCloset) {
                    // if (pageCloset.scale) {
                    //     this.setScaleFactorOnPdfPage(pageCloset.scale);
                    //     if (this.getScaleForPage(pageNum) === null) {
                    //         this.setPageScale(
                    //             pageNum,
                    //             pageCloset.scale,
                    //             ScaleSource.SAVED,
                    //         );
                    //     }
                    // }
                    this.addExistingClosets(
                        pageCloset.id,
                        pageCloset.points,
                        pageNum,
                        pageCloset.unitId,
                        pageCloset.name,
                        pageCloset.scale,
                    );

                    if (Array.isArray(pageCloset.attachments)) {
                        pageCloset.attachments.forEach((attachment) => {
                            this.addExistingAttachments(
                                attachment.id,
                                pageCloset.id,
                                attachment.points,
                                pageNum,
                            );
                            this.setAttachmentAttributes(
                                attachment.attachmentType,
                                attachment.type,
                                attachment.opening,
                                attachment.swing,
                            );
                        });
                    }

                    if (Array.isArray(pageCloset.obstacles)) {
                        pageCloset.obstacles.forEach((obstacle) => {
                            this.addExistingObstacle(
                                obstacle,
                                pageNum,
                                pageCloset.scale,
                                pageCloset.id,
                            );
                        });
                    }
                    if (Array.isArray(pageCloset.internalWalls)) {
                        pageCloset.internalWalls.forEach((wall) => {
                            this.addExistingInternalWall(
                                wall,
                                pageNum,
                                pageCloset.scale,
                                pageCloset.id,
                            );
                        });
                    }
                }
            });
        });
    }
    setScalesOfPages(inScaleArray) {
        inScaleArray.forEach((scale) => {
            this.setPageScale(scale.pageNo, scale.scale, scale.type);
        });
    }
    setUnitsScaleForSelectedPage(inScale) {
        for (let i = 0; i < this.units.length; i++) {
            if (this.units[i].pageNo === this.selectedPageNo) {
                this.setUnitScale(this.units[i].id, inScale);
            }
        }
        message.warning(
            "Changing the scale will override the unit's existing scale.",
        );
    }

    getAttachmentData(coordinate) {
        const closetCanvasRect = this.closetCanvasRect;
        coordinate.x = coordinate.x - closetCanvasRect.left - this.translateX;
        coordinate.y = coordinate.y - closetCanvasRect.top - this.translateY;
        const selectedAttachment = this.attachments.find((attachment) => {
            const x = coordinate.x;
            const y = coordinate.y;
            const [point1, point2] = attachment.coordinates;
            const dist1 = Math.sqrt((point1.x - x) ** 2 + (point1.y - y) ** 2);
            const dist2 = Math.sqrt((point2.x - x) ** 2 + (point2.y - y) ** 2);
            const dist = Math.sqrt(
                (point1.x - point2.x) ** 2 + (point1.y - point2.y) ** 2,
            );
            return dist1 + dist2 - dist < this.tolForDelete;
        });

        this.setSelectedAttachment(selectedAttachment);

        return selectedAttachment;
    }

    getObstacleAndInternalWallData(coordinate, isObstacle = true) {
        const canvasRect = this.closetCanvasRect;
        const x = coordinate.x - canvasRect.left - this.translateX;
        const y = coordinate.y - canvasRect.top - this.translateY;
        if (!isObstacle) {
            const selectedInternalWall = this.internalWalls.find((wall) => {
                const centerX = wall.coordinate.x;
                const centerY = wall.coordinate.y;
                const width = wall.length;
                const height = wall.depth;
                return (
                    x >= centerX - width / 2 &&
                    x <= centerX + width / 2 &&
                    y >= centerY - height / 2 &&
                    y <= centerY + height / 2
                );
            });
            if (selectedInternalWall) {
                this.setInternalWallData(selectedInternalWall);
                return selectedInternalWall;
            }
            return null;
        }
        const selectedObstacle = this.obstacles.find((obstacle) => {
            const centerX = obstacle.data.coordinate.x;
            const centerY = obstacle.data.coordinate.y;
            if (!obstacle.data.radius) {
                const width = obstacle.data.width;
                const height = obstacle.data.height;
                return (
                    x >= centerX - width / 2 &&
                    x <= centerX + width / 2 &&
                    y >= centerY - height / 2 &&
                    y <= centerY + height / 2
                );
            } else {
                const radius = obstacle.data.radius;
                const distance = Math.sqrt(
                    (x - centerX) ** 2 + (y - centerY) ** 2,
                );
                return distance <= radius;
            }
        });
        if (selectedObstacle) {
            this.setSelectedObstacle(selectedObstacle);
            return selectedObstacle;
        }
        return null;
    }

    getUnitData(coordinate) {
        const closetCanvasRect = this.closetCanvasRect;
        coordinate.x = coordinate.x - closetCanvasRect.left - this.translateX;
        coordinate.y = coordinate.y - closetCanvasRect.top - this.translateY;
        const selectedUnit = this.units.find((unit) => {
            const x = coordinate.x;
            const y = coordinate.y;

            for (let i = 0; i < unit.coordinates.length; i++) {
                const point1 = unit.coordinates[i];
                const point2 =
                    unit.coordinates[(i + 1) % unit.coordinates.length];

                const dist1 = Math.sqrt(
                    (point1.x - x) ** 2 + (point1.y - y) ** 2,
                );
                const dist2 = Math.sqrt(
                    (point2.x - x) ** 2 + (point2.y - y) ** 2,
                );
                const dist = Math.sqrt(
                    (point1.x - point2.x) ** 2 + (point1.y - point2.y) ** 2,
                );

                if (dist1 + dist2 - dist < this.tolForDelete) {
                    return true;
                }
            }

            return false;
        });

        this.setSelectedUnit(selectedUnit);

        return selectedUnit;
    }

    updateObstaclePositionWithValidation(
        coordinate,
        isObstacleCoordinate = false,
    ) {
        if (!this.selectedObstacle) this.drawNewClosets();
        if (!isObstacleCoordinate) {
            const closetCanvasRect = this.closetCanvasRect;
            coordinate.x =
                coordinate.x - closetCanvasRect.left - this.translateX;
            coordinate.y =
                coordinate.y - closetCanvasRect.top - this.translateY;
        }
        const obstacle = this.obstacles.find(
            (obstacle) => obstacle.id === this.selectedObstacle.id,
        );
        if (!obstacle) {
            console.warn('Obstacle not found:', this.selectedObstacle.id);
            return;
        }
        const obstacleClosetId = obstacle.closetId;
        const closet = this.closets.find(
            (closet) => closet.id === obstacleClosetId,
        );
        if (!closet) {
            console.warn('Closet not found:', obstacleClosetId);
            return;
        }
        const isInside = Utils.pointInPolygon(coordinate, closet.coordinates);
        if (!isInside) {
            console.warn('Obstacle is Majorly outside the closet');
            throw new Error('Obstacle is Majorly outside the closet');
        }
        if (isInside) {
            obstacle._data.coordinate = coordinate;
            this._obstacles = [...this._obstacles];
        }
        this.drawNewClosets();
    }
    updateInternalWallPositionWithValidation(coordinate) {
        if (!this.internalWallData) this.drawNewClosets();
        const closetCanvasRect = this.closetCanvasRect;
        coordinate.x = coordinate.x - closetCanvasRect.left - this.translateX;
        coordinate.y = coordinate.y - closetCanvasRect.top - this.translateY;
        const internalWall = this.internalWalls.find(
            (internalWall) => internalWall.id === this.internalWallData.id,
        );
        if (!internalWall) {
            console.warn('InternalWall not found:', this.internalWallData.id);
            return;
        }
        const closet = this.closets.find(
            (closet) => closet.id === internalWall.closetId,
        );
        if (!closet) {
            console.warn('Closet not found:');
            return;
        }
        const tol = this.internalWallData.length / 2;
        const nearest = this._libState.closetManager.nearestClosetPoint(
            coordinate,
            tol,
            true,
        );
        let wallPosition = null;
        let wallAngle = 0;
        if (nearest) {
            const edgePoints = this._libState.closetManager.selectedEdgeEnds;
            if (!Utils.isClockwise(closet.coordinates)) {
                edgePoints.reverse();
            }
            const pointOnLine = Utils.isPointOnLine(
                nearest,
                edgePoints[0],
                edgePoints[1],
            );
            if (pointOnLine) {
                const angle = Math.atan2(
                    edgePoints[1].y - edgePoints[0].y,
                    edgePoints[1].x - edgePoints[0].x,
                );
                wallPosition = nearest;
                wallAngle = angle + Math.PI / 2;
                const width = internalWall.length;
                const centerX =
                    wallPosition.x + (width / 2) * Math.cos(wallAngle);
                const centerY =
                    wallPosition.y + (width / 2) * Math.sin(wallAngle);
                const degrees = wallAngle * (180 / Math.PI);
                const normal = {
                    x: Math.cos(wallAngle),
                    y: Math.sin(wallAngle),
                    z: 0,
                };
                const magnitude = Math.sqrt(
                    normal.x * normal.x + normal.y * normal.y,
                );
                normal.x /= magnitude;
                normal.y /= magnitude;
                normal.z = 0;
                internalWall.setNormal(normal);
                internalWall.setRotation(degrees);
                internalWall.setCoordinate({ x: centerX, y: centerY });
            }
        } else {
            const closetPolygon = closet.coordinates;
            const isInsideCloset = Utils.pointInPolygon(
                coordinate,
                closetPolygon,
            );
            if (!isInsideCloset) {
                console.warn('InternalWall is Majorly outside the closet');
                throw new Error('InternalWall is Majorly outside the closet');
            }
            wallPosition = coordinate;
            internalWall.setCoordinate(wallPosition);
        }
        this.drawNewClosets();
    }

    updateObstaclePosition(data) {
        if (!data || !data.left || !data.top) {
            console.warn('Invalid data for updateObstaclePosition:', data);
            return;
        }

        const prevLeft = Utils.parseInches(this.obstaclePosData.left || "0' 0");
        const prevTop = Utils.parseInches(this.obstaclePosData.top || "0' 0");
        const left = Utils.parseInches(data.left || "0' 0");
        const top = Utils.parseInches(data.top || "0' 0");

        let leftDiff = parseFloat((left - prevLeft).toFixed(2));
        let topDiff = parseFloat((top - prevTop).toFixed(2));

        const selectedObstacle = this.selectedObstacle;
        if (!selectedObstacle) {
            console.warn('No selectedObstacle');
            return;
        }

        const obstacle = this.obstacles.find(
            (obstacle) => obstacle.id === selectedObstacle.id,
        );
        if (!obstacle) {
            console.warn('Obstacle not found:', selectedObstacle.id);
            return;
        }

        let { x: centerX, y: centerY } = obstacle.data.coordinate;

        const scaleFactor = this.getScaleFactorForAnnotationDisplayByClosetId(
            this.selectedPageNo,
            obstacle.closetId,
        );

        // Convert inch differences to pixels (assuming 12 pixels per inch)
        let leftXY = leftDiff * 12;
        let topXY = topDiff * 12;

        leftXY = (leftXY * scaleFactor) / 2;
        topXY = (topXY * scaleFactor) / 2;

        centerX += leftXY;
        centerY += topXY;
        const coordinate = { x: centerX, y: centerY };
        this.updateObstaclePositionWithValidation(coordinate, true);
    }

    getTopAndLeft() {
        if (!this.selectedObstacle) {
            console.warn('No selectedObstacle for getTopAndLeft');
            return;
        }

        const obstacle = this.obstacles.find(
            (obstacle) => obstacle.id === this.selectedObstacle.id,
        );
        if (!obstacle) {
            console.warn('Obstacle  not found:', { obstacle, closet });
            return;
        }
        const closet = this.closets.find(
            (closet) => closet.id === obstacle.closetId,
        );

        if (!closet) {
            console.warn('Closet not found:', { obstacle, closet });
            return;
        }

        const { x: centerX, y: centerY } = obstacle.data.coordinate;
        const {
            radius: obstacleRadius,
            width: obstacleWidth,
            height: obstacleHeight,
        } = obstacle.data;
        const closetPolygon = closet.coordinates;

        let horizontalIntersection = [];
        let verticalIntersection = [];
        let horizontalLine = [];
        let verticalLine = [];

        if (!obstacleRadius) {
            horizontalLine = [
                { x: centerX - obstacleWidth / 2, y: centerY },
                { x: centerX + obstacleWidth / 2, y: centerY },
            ];
            verticalLine = [
                { x: centerX, y: centerY - obstacleHeight / 2 },
                { x: centerX, y: centerY + obstacleHeight / 2 },
            ];
        } else {
            horizontalLine = [
                { x: centerX - obstacleRadius, y: centerY },
                { x: centerX + obstacleRadius, y: centerY },
            ];
            verticalLine = [
                { x: centerX, y: centerY - obstacleRadius },
                { x: centerX, y: centerY + obstacleRadius },
            ];
        }

        const scaleFactor =
            this.getScaleForPage(this.selectedPageNo) *
            this.fetchTransformationScaleFactorByPageNo(this.selectedPageNo);

        closetPolygon.forEach((coordinate, index) => {
            const nextIndex = (index + 1) % closetPolygon.length;
            const intersection = CanvasUtils.findIntersection(horizontalLine, [
                coordinate,
                closetPolygon[nextIndex],
            ]);
            if (intersection) {
                horizontalIntersection.push(intersection);
            }
            const intersection2 = CanvasUtils.findIntersection(verticalLine, [
                coordinate,
                closetPolygon[nextIndex],
            ]);
            if (intersection2) {
                verticalIntersection.push(intersection2);
            }
        });

        let left = 0;
        let leftLimit = 0;
        if (horizontalIntersection.length === 2) {
            horizontalIntersection.sort((a, b) => a.x - b.x);
            const diff = obstacleRadius ? obstacleRadius : obstacleWidth / 2;
            const line1 = [
                horizontalIntersection[0],
                { x: centerX - diff, y: centerY },
            ];
            const line2 = [
                horizontalIntersection[1],
                { x: centerX + diff, y: centerY },
            ];

            const dist1 = CanvasUtils.measureDistance(
                line1[0],
                line1[1],
                scaleFactor,
            );
            const dist2 = CanvasUtils.measureDistance(
                line2[0],
                line2[1],
                scaleFactor,
            );

            left = parseFloat(dist1).toFixed(2);
            leftLimit = parseFloat(
                (parseFloat(dist1) + parseFloat(dist2)).toFixed(2),
            );
        }

        let top = 0;
        let topLimit = 0;
        if (verticalIntersection.length === 2) {
            const diff = obstacleRadius ? obstacleRadius : obstacleHeight / 2;
            verticalIntersection.sort((a, b) => a.y - b.y);
            const line1 = [
                { x: centerX, y: centerY - diff },
                verticalIntersection[0],
            ];
            const line2 = [
                { x: centerX, y: centerY + diff },
                verticalIntersection[1],
            ];

            const dist1 = CanvasUtils.measureDistance(
                line1[0],
                line1[1],
                scaleFactor,
            );
            const dist2 = CanvasUtils.measureDistance(
                line2[0],
                line2[1],
                scaleFactor,
            );

            top = parseFloat(dist1).toFixed(2);
            topLimit = parseFloat(
                (parseFloat(dist1) + parseFloat(dist2)).toFixed(2),
            );
        }

        const data = {
            left: `0' ${left}"`,
            leftLimit: `0' ${leftLimit}"`,
            top: `0' ${top}"`,
            topLimit: `0' ${topLimit}"`,
        };

        this.setObstaclePosData(data);
    }

    showPossibleClosets(uuid) {
        const possibleClosets =
            this._libState.viewManager.selectedPagePossibleClosets;
        if (!possibleClosets) {
            console.warn('No possible closets found.');
            return;
        }

        const closetCanvasRect = this.closetCanvasRect;
        if (!closetCanvasRect) {
            console.error('Closet canvas rect is not defined.');
            return;
        }

        const closet = possibleClosets.find((closet) => closet.uuid === uuid);
        if (!closet) {
            console.warn(`Closet with UUID ${uuid} not found.`);
            return;
        }

        const coordinates = closet.bbox;
        if (!coordinates || coordinates.length < 4) {
            console.error('Invalid bounding box coordinates:', coordinates);
            return;
        }

        const padding = 100;
        const bboxWidth = coordinates[2] - coordinates[0];
        const bboxHeight = coordinates[3] - coordinates[1];
        const paddedWidth = bboxWidth + 2 * padding;
        const paddedHeight = bboxHeight + 2 * padding;

        const scaleX = closetCanvasRect.width / paddedWidth;
        const scaleY = closetCanvasRect.height / paddedHeight;
        const newScaleFactor = Math.min(scaleX, scaleY);
        if (newScaleFactor <= 0) {
            console.error('Invalid computed scale factor:', newScaleFactor);
            return;
        }
        this._scaleForZoom =
            this.fetchTransformationScaleFactorByPageNo(this.selectedPageNo) /
            newScaleFactor;
        this.setScaleFactorByPageNo(this.selectedPageNo, newScaleFactor);

        const closetCenterX = (coordinates[0] + coordinates[2]) / 2;
        const closetCenterY = (coordinates[1] + coordinates[3]) / 2;
        const transformedCenterX = closetCenterX * newScaleFactor;
        const transformedCenterY = closetCenterY * newScaleFactor;

        const viewportCenterX = closetCanvasRect.width / 2;
        const viewportCenterY = closetCanvasRect.height / 2;

        const transX = viewportCenterX - transformedCenterX;
        const transY = viewportCenterY - transformedCenterY;

        this.setTranslateX(transX);
        this.setTranslateY(transY);

        this.clearClosetCanvas();
        this.renderPage();
        this._libState.drawingManager.transformAnnotationCanvas();
        this.drawAllClosets();

        const ctx = this.closetCanvas.getContext('2d');
        if (!ctx) {
            console.error('Failed to get canvas context');
            return;
        }

        CanvasUtils.drawRectangle(
            ctx,
            { x: transformedCenterX, y: transformedCenterY },
            bboxWidth * newScaleFactor,
            bboxHeight * newScaleFactor,
            'blue',
            0.5,
        );
    }

    async showClosets(id) {
        const closets = this.closets;
        if (!closets) {
            console.warn('No closets found.');
            return;
        }

        const closetCanvasRect = this.closetCanvasRect;
        if (!closetCanvasRect) {
            console.error('Closet canvas rect is not defined.');
            return;
        }

        const closet = closets.find((closet) => closet.id === id);
        if (!closet) {
            console.warn(`Closet with ID ${id} not found.`);
            return;
        }

        if (closet.pageNo !== this.selectedPageNo) {
            this.setIsLoading(true);
            this._libState.viewManager.setActivePageNo(closet.pageNo);
            await this._libState.viewManager.bringChangesToClosetAccordingToSelectedPages();
            await this.setSelectedPageNo(closet.pageNo);
            await this.fitToScreen();
            this._libState.viewManager.updatePossibleClosets();
            this.setIsLoading(false);
        }

        const scale = this.fetchTransformationScaleFactorByPageNo(
            this.selectedPageNo,
        );

        this._libState.operationsManager.scaleClosets(1 / scale);

        const coordinates = JSON.parse(JSON.stringify(closet.coordinates));

        this._libState.operationsManager.scaleClosets(scale);
        if (!coordinates || coordinates.length < 2) {
            console.error('Invalid coordinates:', coordinates);
            return;
        }

        let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
        coordinates.forEach((coord) => {
            minX = Math.min(minX, coord.x);
            minY = Math.min(minY, coord.y);
            maxX = Math.max(maxX, coord.x);
            maxY = Math.max(maxY, coord.y);
        });

        const bbox = [minX, minY, maxX, maxY];
        if (bbox[2] <= bbox[0] || bbox[3] <= bbox[1]) {
            console.error('Invalid bounding box calculated:', bbox);
            return;
        }

        const padding = 100;
        const bboxWidth = bbox[2] - bbox[0];
        const bboxHeight = bbox[3] - bbox[1];
        const paddedWidth = bboxWidth + 2 * padding;
        const paddedHeight = bboxHeight + 2 * padding;

        const scaleX = closetCanvasRect.width / paddedWidth;
        const scaleY = closetCanvasRect.height / paddedHeight;
        const newScaleFactor = Math.min(scaleX, scaleY);
        if (newScaleFactor <= 0) {
            console.error('Invalid computed scale factor:', newScaleFactor);
            return;
        }

        this._scaleForZoom =
            this.fetchTransformationScaleFactorByPageNo(this.selectedPageNo) /
            newScaleFactor;
        this.setScaleFactorByPageNo(this.selectedPageNo, newScaleFactor);

        const closetCenterX = (bbox[0] + bbox[2]) / 2;
        const closetCenterY = (bbox[1] + bbox[3]) / 2;
        const transformedCenterX = closetCenterX * newScaleFactor;
        const transformedCenterY = closetCenterY * newScaleFactor;

        const viewportCenterX = closetCanvasRect.width / 2;
        const viewportCenterY = closetCanvasRect.height / 2;

        const transX = viewportCenterX - transformedCenterX;
        const transY = viewportCenterY - transformedCenterY;

        this.setTranslateX(transX);
        this.setTranslateY(transY);

        this.clearClosetCanvas();
        this.renderPage();
        this._libState.drawingManager.transformAnnotationCanvas();
        this.drawAllClosets();

        const ctx = this.closetCanvas.getContext('2d');
        if (!ctx) {
            console.error('Failed to get canvas context');
            return;
        }

        CanvasUtils.drawRectangle(
            ctx,
            { x: transformedCenterX, y: transformedCenterY },
            bboxWidth * newScaleFactor,
            bboxHeight * newScaleFactor,
            'red',
            0.25,
        );

        this.drawSnapPoints();
    }

    getUnitIdByClosetId(closetId) {
        const unit = this.closets.find((closet) => closet.id === closetId);
        return unit ? unit.unitId : null;
    }

    async showUnits(id) {
        const units = this.units;
        if (!units) {
            console.warn('No units found.');
            return;
        }

        const closetCanvasRect = this.closetCanvasRect;
        if (!closetCanvasRect) {
            console.error('Closet canvas rect is not defined.');
            return;
        }

        const unit = units.find((unit) => unit.id === id);
        if (!unit) {
            console.warn(`Closet with ID ${id} not found.`);
            return;
        }

        if (unit.pageNo !== this.selectedPageNo) {
            this.setIsLoading(true);
            this._libState.viewManager.setActivePageNo(unit.pageNo);
            await this._libState.viewManager.bringChangesToClosetAccordingToSelectedPages();
            await this.setSelectedPageNo(unit.pageNo);
            await this.fitToScreen();
            this._libState.viewManager.updatePossibleClosets();
            this.setIsLoading(false);
        }

        const scale = this.fetchTransformationScaleFactorByPageNo(
            this.selectedPageNo,
        );

        this._libState.operationsManager.scaleUnits(1 / scale);

        const coordinates = JSON.parse(JSON.stringify(unit.coordinates));

        this._libState.operationsManager.scaleUnits(scale);
        if (!coordinates || coordinates.length < 2) {
            console.error('Invalid coordinates:', coordinates);
            return;
        }

        // Calculate bounding box from coordinates
        let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;
        coordinates.forEach((coord) => {
            minX = Math.min(minX, coord.x);
            minY = Math.min(minY, coord.y);
            maxX = Math.max(maxX, coord.x);
            maxY = Math.max(maxY, coord.y);
        });

        const bbox = [minX, minY, maxX, maxY];
        if (bbox[2] <= bbox[0] || bbox[3] <= bbox[1]) {
            console.error('Invalid bounding box calculated:', bbox);
            return;
        }

        const padding = 100;
        const bboxWidth = bbox[2] - bbox[0];
        const bboxHeight = bbox[3] - bbox[1];
        const paddedWidth = bboxWidth + 2 * padding;
        const paddedHeight = bboxHeight + 2 * padding;

        const scaleX = closetCanvasRect.width / paddedWidth;
        const scaleY = closetCanvasRect.height / paddedHeight;
        const newScaleFactor = Math.min(scaleX, scaleY);
        if (newScaleFactor <= 0) {
            console.error('Invalid computed scale factor:', newScaleFactor);
            return;
        }

        this._scaleForZoom =
            this.fetchTransformationScaleFactorByPageNo(this.selectedPageNo) /
            newScaleFactor;
        this.setScaleFactorByPageNo(this.selectedPageNo, newScaleFactor);

        const closetCenterX = (bbox[0] + bbox[2]) / 2;
        const closetCenterY = (bbox[1] + bbox[3]) / 2;
        const transformedCenterX = closetCenterX * newScaleFactor;
        const transformedCenterY = closetCenterY * newScaleFactor;

        const viewportCenterX = closetCanvasRect.width / 2;
        const viewportCenterY = closetCanvasRect.height / 2;

        const transX = viewportCenterX - transformedCenterX;
        const transY = viewportCenterY - transformedCenterY;

        this.setTranslateX(transX);
        this.setTranslateY(transY);

        this.clearClosetCanvas();
        this.renderPage();
        this._libState.drawingManager.transformAnnotationCanvas();
        this.drawAllClosets();

        const ctx = this.closetCanvas.getContext('2d');
        if (!ctx) {
            console.error('Failed to get canvas context');
            return;
        }

        CanvasUtils.drawRectangle(
            ctx,
            { x: transformedCenterX, y: transformedCenterY },
            bboxWidth * newScaleFactor,
            bboxHeight * newScaleFactor,
            'yellow',
            0.25,
        );

        this.drawSnapPoints();
    }

    showPresentUnits(uuid) {
        let presentUnits = [];

        Object.values(this.presentUnits).forEach((keyWord) => {
            keyWord.forEach((unit) => {
                presentUnits.push(unit);
            });
        });
        if (!presentUnits) {
            console.warn('No possible closets found.');
            return;
        }

        const closetCanvasRect = this.closetCanvasRect;
        if (!closetCanvasRect) {
            console.error('Closet canvas rect is not defined.');
            return;
        }

        const closet = presentUnits.find((closet) => closet.uuid === uuid);
        if (!closet) {
            console.warn(`Closet with UUID ${uuid} not found.`);
            return;
        }

        const coordinates = closet.bbox;
        if (!coordinates || coordinates.length < 4) {
            console.error('Invalid bounding box coordinates:', coordinates);
            return;
        }

        const padding = 100;
        const bboxWidth = coordinates[2] - coordinates[0];
        const bboxHeight = coordinates[3] - coordinates[1];
        const paddedWidth = bboxWidth + 2 * padding;
        const paddedHeight = bboxHeight + 2 * padding;

        const scaleX = closetCanvasRect.width / paddedWidth;
        const scaleY = closetCanvasRect.height / paddedHeight;
        const newScaleFactor = Math.min(scaleX, scaleY);
        if (newScaleFactor <= 0) {
            console.error('Invalid computed scale factor:', newScaleFactor);
            return;
        }
        this._scaleForZoom =
            this.fetchTransformationScaleFactorByPageNo(this.selectedPageNo) /
            newScaleFactor;
        this.setScaleFactorByPageNo(this.selectedPageNo, newScaleFactor);

        const closetCenterX = (coordinates[0] + coordinates[2]) / 2;
        const closetCenterY = (coordinates[1] + coordinates[3]) / 2;
        const transformedCenterX = closetCenterX * newScaleFactor;
        const transformedCenterY = closetCenterY * newScaleFactor;

        const viewportCenterX = closetCanvasRect.width / 2;
        const viewportCenterY = closetCanvasRect.height / 2;

        const transX = viewportCenterX - transformedCenterX;
        const transY = viewportCenterY - transformedCenterY;

        this.setTranslateX(transX);
        this.setTranslateY(transY);

        this.clearClosetCanvas();
        this.renderPage();
        this._libState.drawingManager.transformAnnotationCanvas();
        this.drawAllClosets();

        const ctx = this.closetCanvas.getContext('2d');
        if (!ctx) {
            console.error('Failed to get canvas context');
            return;
        }

        CanvasUtils.drawRectangle(
            ctx,
            { x: transformedCenterX, y: transformedCenterY },
            bboxWidth * newScaleFactor,
            bboxHeight * newScaleFactor,
            'blue',
            0.5,
        );
    }
    getUnitsInSelectedPage() {
        return this.units.filter((unit) => unit.pageNo === this.selectedPageNo)
            .length;
    }
    updatePresentUnitData() {
        let presentUnits = {};

        this.units.forEach((unit) => {
            Object.entries(this.allUnitData).forEach(([pageNo, Page]) => {
                Page.forEach((subUnit) => {
                    if (subUnit.text.includes(unit.name)) {
                        if (!presentUnits[unit.name]) {
                            presentUnits[unit.name] = []; // Initialize array if not exists
                        }
                        presentUnits[unit.name].push({
                            bbox: subUnit.bbox,
                            rotation: subUnit.rotation,
                            pageNo: Number(pageNo),
                            uuid: subUnit.uuid,
                        });
                    }
                });
            });
        });

        this.setPresentUnits(presentUnits);
    }
    selectedPageCloset() {
        const selectedClosets = this.closets.filter(
            (closet) => closet.pageNo === this.selectedPageNo,
        );
        return selectedClosets;
    }

    selectedPageUnit() {
        const selectedUnits = this.units.filter(
            (unit) => unit.pageNo === this.selectedPageNo,
        );
        return selectedUnits;
    }

    selectedPageAttachment() {
        const selectedAttachments = this.attachments.filter(
            (attachment) => attachment.pageNo === this.selectedPageNo,
        );
        return selectedAttachments;
    }

    selectedPageObstacle() {
        const selectedObstacles = this.obstacles.filter(
            (obstacle) => obstacle.pageNo === this.selectedPageNo,
        );
        return selectedObstacles;
    }

    excludeClosets() {
        //remove the units from the units array whose unit.pageNum is not present in selectedPages array(page.pageNum)
        this._units = this.units.filter((unit) =>
            this._libState.viewManager.selectedPages.some(
                (page) => page.pageNum == unit.pageNo,
            ),
        );
        this._units = [...this.units];
        this._closets = this.closets.filter((closet) =>
            this._libState.viewManager.selectedPages.some(
                (page) => page.pageNum == closet.pageNo,
            ),
        );
        this._closets = [...this.closets];
        this.closetValidation();
    }
    async createSelectedPagePdfBuffer() {
        try {
            let buffer =
                this.pdfBuffer instanceof File
                    ? await this.pdfBuffer.arrayBuffer()
                    : this.pdfBuffer;

            if (!buffer) {
                throw new Error('No valid PDF file loaded.');
            }

            const pageNumbers = this._libState.viewManager.selectedPages
                .map((p) => Number(p.pageNum))
                .filter((n) => !isNaN(n) && n > 0);

            if (pageNumbers.length === 0) {
                throw new Error('No pages selected.');
            }

            const pdfBytes = await PdfUtils.createPdfWithSelectedPages(
                buffer,
                pageNumbers,
            );

            this.setPdfBuffer(
                new File([pdfBytes], 'selected_pages.pdf', {
                    type: 'application/pdf',
                }),
            );
        } catch (error) {
            console.error('createSelectedPagePdfBuffer error:', error);
            throw new Error(`Error creating PDF: ${error.message}`);
        }
    }

    async createSelectedPagePdfBufferFromViewManagerPdf() {
        try {
            let buffer =
                this._libState.viewManager.pdfFile instanceof Blob
                    ? await this._libState.viewManager.pdfFile.arrayBuffer()
                    : this._libState.viewManager.pdfFile;

            if (!buffer) {
                throw new Error('No valid PDF data found in viewManager');
            }

            const pageNumbers = this._libState.viewManager.selectedPages
                .map((p) => Number(p.pageNum))
                .filter((n) => !isNaN(n) && n > 0);

            if (pageNumbers.length === 0) {
                throw new Error('No pages selected.');
            }

            const pdfBytes = await PdfUtils.createPdfWithSelectedPages(
                buffer,
                pageNumbers,
            );

            const file = new File([pdfBytes], 'selected_pages.pdf', {
                type: 'application/pdf',
            });

            this.setPdfBuffer(file);
            const pdfDoc = await Utils.readPdf(file);
            this.setPdfDoc(pdfDoc);
        } catch (error) {
            console.error('createSelectedPagePdfBuffer error:', error);
            throw new Error(`Error creating PDF: ${error.message}`);
        }
    }

    unitNameById(id) {
        return this.units.find((unit) => unit.id === id)?._name;
    }

    lengthValidation() {
        let bool = false;
        for (let i = 0; i < this.closets.length; i++) {
            const closetPolygon = this.closets[i].coordinates;

            let scale = this.fetchTransformationScaleFactorByPageNo(
                this.closets[i].pageNo,
            );
            if (this.selectedPageNo === this.closets[i].pageNo) {
                scale = this.getScaleFactorForAnnotationDisplayByClosetId(
                    this.selectedPageNo,
                    this.closets[i].id,
                );
            }
            if (CanvasUtils.lengthValidation(closetPolygon, scale)) {
                bool = true;
                message.error(
                    `Edge length of the closet "${this.closets[i].name}" in unit "${this.unitNameById(this.closets[i].unitId)}" on page "${this.closets[i].pageNo}" is very long.`,
                );
            }
        }
        return bool;
    }

    doorIntersectionCheck(point) {
        for (let i = 0; i < this.attachments.length; i++) {
            const doorPolygon = this.attachments[i].coordinates;
            if (Utils.isPointOnLine(point, doorPolygon[0], doorPolygon[1])) {
                return true;
            }
        }
        return false;
    }
    doorOverlapCheck(newStart, newEnd) {
        for (let i = 0; i < this.attachments.length; i++) {
            const doorSegment = this.attachments[i].coordinates;
            const existingStart = doorSegment[0];
            const existingEnd = doorSegment[1];

            if (
                Utils.doLineSegmentsOverlap(
                    newStart,
                    newEnd,
                    existingStart,
                    existingEnd,
                )
            ) {
                return true;
            }
        }
        return false;
    }

    doorIntersectionCheckForExistingAttachment(point, attachment) {
        for (let i = 0; i < this.attachments.length; i++) {
            if (this.attachments[i].id === attachment.id) {
                continue;
            }
            const doorPolygon = this.attachments[i].coordinates;
            if (Utils.isPointOnLine(point, doorPolygon[0], doorPolygon[1])) {
                return true;
            }
        }
        return false;
    }

    drawPredictedClosets = (data, pageWidth, pageHeight, pageNo) => {
        if (!data || data.length === 0) {
            message.error('No closets found.');
            return;
        }
        const scale = this.fetchTransformationScaleFactorByPageNo(pageNo);
        const TOLERANCE = 3.5;
        // const fullPagePolygon = [
        //     { x: 0 * scale, y: 0 * scale }, // Bottom-left
        //     { x: pageWidth * scale, y: 0 * scale }, // Bottom-right
        //     { x: pageWidth * scale, y: pageHeight * scale }, // Top-right
        //     { x: 0 * scale, y: pageHeight * scale }, // Top-left
        // ];

        // this.addUnit('Predicted-Unit', fullPagePolygon);

        data.forEach((item, index) => {
            const { xmin, xmax, ymin, ymax } = item.bbox;
            const unitPolygon = [
                {
                    x: (xmin - TOLERANCE) * scale,
                    y: (ymin - TOLERANCE) * scale,
                },
                {
                    x: (xmax + TOLERANCE) * scale,
                    y: (ymin - TOLERANCE) * scale,
                },
                {
                    x: (xmax + TOLERANCE) * scale,
                    y: (ymax + TOLERANCE) * scale,
                },
                {
                    x: (xmin - TOLERANCE) * scale,
                    y: (ymax + TOLERANCE) * scale,
                },
            ];
            this.addUnit(`unit-around-closet-${index}`, unitPolygon);
            const closetPolygon = [
                { x: xmin * scale, y: ymin * scale }, // Bottom-left
                { x: xmax * scale, y: ymin * scale }, // Bottom-right
                { x: xmax * scale, y: ymax * scale }, // Top-right
                { x: xmin * scale, y: ymax * scale }, // Top-left
            ];
            this.addCloset(`closet-${index}`, closetPolygon);
        });
    };

    async downloadAnnotatedPdf() {
        if (!this._libState.viewManager.pdfFile) {
            message.error(
                'Full PDF has not arrived yet. Please wait for the complete PDF to load.',
            );
            return;
        }

        try {
            let pdfBytes;
            if (this._libState.viewManager.pdfFile instanceof Blob) {
                pdfBytes =
                    await this._libState.viewManager.pdfFile.arrayBuffer();
            } else if (
                this._libState.viewManager.pdfFile instanceof ArrayBuffer
            ) {
                pdfBytes = this._libState.viewManager.pdfFile;
            } else if (
                this._libState.viewManager.pdfFile instanceof Uint8Array
            ) {
                pdfBytes = this._libState.viewManager.pdfFile;
            } else {
                message.error('Invalid PDF format in viewManager');
                return;
            }

            const pdfDoc = await PDFDocument.load(pdfBytes);
            const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

            const transformPoint = PdfUtils.transformPoint;
            const baseDrawTextWithBackground = PdfUtils.drawTextWithBackground;
            const baseDrawPolygon = PdfUtils.drawPolygon;
            const baseRotatePointsByPageRotation =
                PdfUtils.rotatePointsByPageRotation;
            const baseDrawDoorSwingInPdf = PdfUtils.drawDoorSwingInPdf;

            const _drawTextWithBackground = (
                page,
                text,
                x,
                y,
                height,
                fontSize = 12,
                isUnit = false,
            ) =>
                baseDrawTextWithBackground(page, text, x, y, height, font, {
                    fontSize,
                    isUnit,
                });
            const _drawDistanceLabel = (
                page,
                point1,
                point2,
                scale,
                height,
                existingLabels,
            ) =>
                PdfUtils.drawDistanceLabel(
                    page,
                    point1,
                    point2,
                    scale,
                    height,
                    font,
                    existingLabels,
                );
            const _drawPolygon = baseDrawPolygon;
            const _rotatePointsByPageRotation = baseRotatePointsByPageRotation;

            for (let pageNum = 1; pageNum <= pdfDoc.getPageCount(); pageNum++) {
                const page = pdfDoc.getPages()[pageNum - 1];
                const pageDetail = await this.pdfDoc.getPage(pageNum);
                const rotation = pageDetail.rotate || 0;
                // CONVERT ROTATION TO RADIAN
                // const rotationInRadians = (rotation * Math.PI) / 180;
                const { width, height } = page.getSize();

                const scaleFactor =
                    this.fetchTransformationScaleFactorByPageNo(pageNum);

                this._libState.operationsManager.scaleClosets(1 / scaleFactor);
                this._libState.operationsManager.scaleAttachments(
                    1 / scaleFactor,
                );
                this._libState.operationsManager.scaleObstacles(
                    1 / scaleFactor,
                );
                this._libState.operationsManager.scaleUnits(1 / scaleFactor);
                this._libState.operationsManager.scaleInternalWalls(
                    1 / scaleFactor,
                );

                const existingLabels = [];

                // Draw polygons

                for (const unit of this._units) {
                    if (
                        unit &&
                        unit.pageNo === pageNum &&
                        Array.isArray(unit._coordinates)
                    ) {
                        const points = unit._coordinates.map((p) => ({
                            x: p.x,
                            y: p.y,
                        }));

                        // Apply page rotation to unit points so they align correctly on rotated pages
                        const rotatedPoints = _rotatePointsByPageRotation(
                            points,
                            rotation,
                            width,
                            height,
                        );

                        if (rotatedPoints.length > 0) {
                            _drawPolygon(
                                page,
                                rotatedPoints,
                                rgb(1, 0.5, 0),
                                2,
                            );

                            if (unit.name) {
                                const center =
                                    Utils.getPolygonCenter(rotatedPoints);
                                _drawTextWithBackground(
                                    page,
                                    unit.name,
                                    center.x,
                                    center.y,
                                    height,
                                    14,
                                    true,
                                );
                            }
                        }
                    }
                }

                for (const closet of this._closets) {
                    if (
                        closet &&
                        closet.pageNo === pageNum &&
                        Array.isArray(closet._coordinates)
                    ) {
                        const points = closet._coordinates.map((p) => ({
                            x: p.x,
                            y: p.y,
                        }));

                        const rotatedPoints = _rotatePointsByPageRotation(
                            points,
                            rotation,
                            width,
                            height,
                        );

                        if (rotatedPoints.length > 0) {
                            _drawPolygon(page, rotatedPoints, rgb(1, 0, 0), 2);
                            if (closet.name) {
                                const center =
                                    Utils.getPolygonCenter(rotatedPoints);
                                _drawTextWithBackground(
                                    page,
                                    closet.name,
                                    center.x,
                                    center.y,
                                    height,
                                    12,
                                    false,
                                );
                            }
                        }
                    }
                }

                for (const attachment of this._attachments) {
                    if (
                        attachment &&
                        attachment.pageNo === pageNum &&
                        Array.isArray(attachment._coordinates)
                    ) {
                        const pointsRaw = attachment._coordinates.map((p) => ({
                            x: p.x,
                            y: p.y,
                        }));
                        const rotatedPoints = _rotatePointsByPageRotation(
                            pointsRaw,
                            rotation,
                            width,
                            height,
                        );

                        if (rotatedPoints.length > 0) {
                            const closet = this._closets.find(
                                (c) => c.id === attachment.closetId,
                            );
                            let rotatedCloset = null;
                            if (closet) {
                                rotatedCloset = _rotatePointsByPageRotation(
                                    closet._coordinates.map((p) => ({
                                        x: p.x,
                                        y: p.y,
                                    })),
                                    rotation,
                                    width,
                                    height,
                                );
                            }
                            if (
                                attachment.attachmentType ===
                                AttachmentType.DOOR
                            ) {
                                baseDrawDoorSwingInPdf(
                                    page,
                                    rotatedPoints[0],
                                    rotatedPoints[1],
                                    attachment._opening,
                                    rotatedCloset || [],
                                    rgb(0, 0.5, 0),
                                    attachment.type,
                                    attachment.swing,
                                    height,
                                );
                            } else {
                                _drawPolygon(
                                    page,
                                    rotatedPoints,
                                    rgb(0, 0, 0.5),
                                    7,
                                );
                            }

                            rotatedPoints.forEach((point) => {
                                const transformedPoint = transformPoint(
                                    point,
                                    height,
                                );
                                page.drawCircle({
                                    x: transformedPoint.x,
                                    y: transformedPoint.y,
                                    size: 5,
                                    color: rgb(1, 0, 0),
                                    borderColor: rgb(1, 0, 0),
                                    borderWidth: 1,
                                });
                            });
                        }
                    }
                }

                for (const obstacle of this._obstacles) {
                    if (
                        obstacle &&
                        obstacle.pageNo === pageNum &&
                        obstacle.data &&
                        obstacle.data.coordinate
                    ) {
                        const coordinate = obstacle.data.coordinate;

                        // Apply page rotation to obstacle coordinate
                        const rotatedCoordinate = _rotatePointsByPageRotation(
                            [coordinate],
                            rotation,
                            width,
                            height,
                        )[0];

                        const transformedCoordinate = transformPoint(
                            rotatedCoordinate,
                            height,
                        );

                        let color = rgb(0, 0.5, 0);
                        if (
                            this.selectedObstacle &&
                            this.selectedObstacle.id === obstacle.id
                        ) {
                            color = rgb(0, 0, 1);
                        }

                        if (!obstacle.data.radius) {
                            const width_obstacle = obstacle.data.width;
                            const height_obstacle = obstacle.data.height;
                            const obstacleRotation =
                                Number(obstacle.data.rotation) || 0;

                            // Combine obstacle rotation with page rotation
                            const totalRotation =
                                -obstacleRotation + (rotation * Math.PI) / 180;

                            const stepSize =
                                Math.min(width_obstacle, height_obstacle) / 15;
                            const halfWidth = width_obstacle / 2;
                            const halfHeight = height_obstacle / 2;

                            for (
                                let x = -halfWidth;
                                x < halfWidth;
                                x += stepSize
                            ) {
                                for (
                                    let y = -halfHeight;
                                    y < halfHeight;
                                    y += stepSize
                                ) {
                                    const rotatedX =
                                        x * Math.cos(totalRotation) -
                                        y * Math.sin(totalRotation);
                                    const rotatedY =
                                        x * Math.sin(totalRotation) +
                                        y * Math.cos(totalRotation);

                                    const pdfX =
                                        transformedCoordinate.x + rotatedX;
                                    const pdfY =
                                        transformedCoordinate.y + rotatedY;

                                    page.drawRectangle({
                                        x: pdfX - stepSize / 2,
                                        y: pdfY - stepSize / 2,
                                        width: stepSize,
                                        height: stepSize,
                                        color: color,
                                        opacity: 0.3,
                                    });
                                }
                            }
                        } else {
                            const radius = obstacle.data.radius;

                            page.drawCircle({
                                x: transformedCoordinate.x,
                                y: transformedCoordinate.y,
                                size: radius,
                                color: color,
                                opacity: 0.3,
                            });
                        }
                    }
                }

                for (const internalWall of this._internalWalls) {
                    if (
                        internalWall &&
                        internalWall.pageNo === pageNum &&
                        internalWall.coordinate
                    ) {
                        const coordinate = internalWall.coordinate;

                        // Apply page rotation to internal wall coordinate
                        const rotatedCoordinate = _rotatePointsByPageRotation(
                            [coordinate],
                            rotation,
                            width,
                            height,
                        )[0];

                        const transformedCoordinate = transformPoint(
                            rotatedCoordinate,
                            height,
                        );

                        let color = rgb(0, 0, 0.5);
                        if (
                            this.internalWallData &&
                            this.internalWallData.id === internalWall.id
                        ) {
                            color = rgb(0, 0, 1);
                        }

                        const width_wall = internalWall.length;
                        const height_obstacle = internalWall.depth;
                        const wallRotation =
                            -(internalWall.rotation * Math.PI) / 180 || 0;

                        // Combine wall rotation with page rotation
                        const totalRotation =
                            wallRotation + (rotation * Math.PI) / 180;

                        const stepSize =
                            Math.min(width_wall, height_obstacle) / 15;
                        const halfWidth = width_wall / 2;
                        const halfHeight = height_obstacle / 2;

                        for (let x = -halfWidth; x < halfWidth; x += stepSize) {
                            for (
                                let y = -halfHeight;
                                y < halfHeight;
                                y += stepSize
                            ) {
                                const localX = x;
                                const localY = y;

                                // Rotate around wall center with combined rotation
                                const rotatedX =
                                    localX * Math.cos(totalRotation) -
                                    localY * Math.sin(totalRotation);
                                const rotatedY =
                                    localX * Math.sin(totalRotation) +
                                    localY * Math.cos(totalRotation);

                                const pdfX = transformedCoordinate.x + rotatedX;
                                const pdfY = transformedCoordinate.y + rotatedY;

                                page.drawRectangle({
                                    x: pdfX - stepSize / 2,
                                    y: pdfY - stepSize / 2,
                                    width: stepSize,
                                    height: stepSize,
                                    color: color,
                                    opacity: 0.3,
                                });
                            }
                        }
                    }
                }

                // Draw distance labels

                for (const unit of this._units) {
                    if (
                        unit &&
                        unit.pageNo === pageNum &&
                        Array.isArray(unit._coordinates)
                    ) {
                        const points = unit._coordinates.map((p) => ({
                            x: p.x,
                            y: p.y,
                        }));
                        const rotatedPoints = _rotatePointsByPageRotation(
                            points,
                            rotation,
                            width,
                            height,
                        );
                        const unitScale = this.getScaleForUnit(unit.id);
                        for (let i = 0; i < rotatedPoints.length; i++) {
                            const point1 = rotatedPoints[i];
                            const point2 =
                                rotatedPoints[(i + 1) % rotatedPoints.length];
                            _drawDistanceLabel(
                                page,
                                point1,
                                point2,
                                unitScale,
                                height,
                                existingLabels,
                            );
                        }
                    }
                }
                for (const closet of this._closets) {
                    if (
                        closet &&
                        closet.pageNo === pageNum &&
                        Array.isArray(closet._coordinates)
                    ) {
                        const points = closet._coordinates.map((p) => ({
                            x: p.x,
                            y: p.y,
                        }));
                        const rotatedPoints = _rotatePointsByPageRotation(
                            points,
                            rotation,
                            width,
                            height,
                        );
                        const closetScale = closet.unitId
                            ? this.getScaleForUnit(closet.unitId)
                            : this.getScaleForPage(pageNum);
                        for (let i = 0; i < rotatedPoints.length; i++) {
                            const point1 = rotatedPoints[i];
                            const point2 =
                                rotatedPoints[(i + 1) % rotatedPoints.length];
                            _drawDistanceLabel(
                                page,
                                point1,
                                point2,
                                closetScale,
                                height,
                                existingLabels,
                            );
                        }
                    }
                }

                for (const attachment of this._attachments) {
                    if (
                        attachment &&
                        attachment.pageNo === pageNum &&
                        Array.isArray(attachment._coordinates)
                    ) {
                        const pointsRaw = attachment._coordinates.map((p) => ({
                            x: p.x,
                            y: p.y,
                        }));
                        const rotatedPoints = _rotatePointsByPageRotation(
                            pointsRaw,
                            rotation,
                            width,
                            height,
                        );
                        const attScale = this.getScaleUnitByClosetId(
                            attachment.closetId,
                        );
                        for (let i = 0; i < rotatedPoints.length - 1; i++) {
                            const point1 = rotatedPoints[i];
                            const point2 = rotatedPoints[i + 1];
                            _drawDistanceLabel(
                                page,
                                point1,
                                point2,
                                attScale,
                                height,
                                existingLabels,
                            );
                        }
                    }
                }

                this._libState.operationsManager.scaleClosets(scaleFactor);
                this._libState.operationsManager.scaleAttachments(scaleFactor);
                this._libState.operationsManager.scaleObstacles(scaleFactor);
                this._libState.operationsManager.scaleInternalWalls(
                    scaleFactor,
                );
                this._libState.operationsManager.scaleUnits(scaleFactor);
            }

            const modifiedPdfBytes = await pdfDoc.save();
            const projectName =
                this._libState.viewManager.formData.projectName ||
                'annotated_floorplan';
            DownloadUtils.downloadPDF(
                modifiedPdfBytes,
                `${projectName}_annotated_floorplan`,
            );
        } catch (error) {
            console.error('Error generating annotated PDF:', error);
            if (error.stack) {
                console.error('Error stack:', error.stack);
                message.error('Error generating annotated PDF.', error.stack);
            }
            message.error('Error generating annotated PDF.', error);
        }
    }

    getScaleFactorForAnnotationDisplayByUnitId = (
        pageNo,
        unitId,
        useDefaultScaleFactor = false,
    ) => {
        return (
            (this.getScaleForUnit(unitId) *
                this.fetchTransformationScaleFactorByPageNo(pageNo)) /
            (useDefaultScaleFactor ? DEFAULT_SCALEFACTOR : 1)
        );
    };

    getScaleFactorForAnnotationDisplayByClosetId = (
        pageNo,
        closetId,
        useDefaultScaleFactor = false,
    ) => {
        return (
            (this.getScaleUnitByClosetId(closetId) *
                this.fetchTransformationScaleFactorByPageNo(pageNo)) /
            (useDefaultScaleFactor ? DEFAULT_SCALEFACTOR : 1)
        );
    };
}
